import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import ErrorIcon from "@mui/icons-material/Error";
import DownloadIcon from "@mui/icons-material/Download";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import {
  Card,
  Box,
  CircularProgress,
  Chip,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import * as FileSaver from "file-saver";
import writeExcelFile from "write-excel-file/browser";
import { useHistory, useLocation } from "react-router-dom";
import api from "../../service/api";
import CONFIG from "../../config";
import {
  BULK_UPLOAD_RESULT_POLLING_INTERVAL,
  BULK_UPLOAD_RESULT_TOTAL_TIME,
  BULK_UPLOAD_RESULT_AUTO_REFRESH_INTERVAL,
} from "../../Constant/BulkOpsContant";

const TABS = ["EPH", "VENDOR", "NOT FOUND", "NO ACTION"];

const VENDOR_SEARCH_COUNTRY_PREFIXES = [
  "#IQVIA_US",
  "#IQVIA",
  "#Veeva Network",
  "#OPEN-DATA",
];

function HoverCell({ label, primary, details }) {
  const [pos, setPos] = useState(null);
  const iconRef = useRef(null);
  const closeTimerRef = useRef(null);
  const HOVER_CARD_WIDTH = 240;
  const HOVER_CARD_MAX_HEIGHT = 280;
  const VIEWPORT_GAP = 8;

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    clearCloseTimer();
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const clampedX = Math.min(
        Math.max(VIEWPORT_GAP, rect.left),
        viewportWidth - HOVER_CARD_WIDTH - VIEWPORT_GAP,
      );

      const spaceBelow = viewportHeight - rect.bottom - VIEWPORT_GAP;
      const spaceAbove = rect.top - VIEWPORT_GAP;
      const prefersTop = spaceBelow < 180 && spaceAbove > spaceBelow;
      const y = prefersTop
        ? Math.max(VIEWPORT_GAP, rect.top - HOVER_CARD_MAX_HEIGHT - 6)
        : Math.min(
            rect.bottom + 6,
            viewportHeight - HOVER_CARD_MAX_HEIGHT - VIEWPORT_GAP,
          );

      const maxHeight = Math.max(
        180,
        Math.min(HOVER_CARD_MAX_HEIGHT, prefersTop ? spaceAbove : spaceBelow),
      );

      setPos({ x: clampedX, y, maxHeight });
    }
  }, [clearCloseTimer]);

  const handleMouseLeave = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setPos(null);
    }, 120);
  }, [clearCloseTimer]);

  const handleCardMouseEnter = useCallback(() => {
    clearCloseTimer();
  }, [clearCloseTimer]);

  const handleCardMouseLeave = useCallback(() => {
    setPos(null);
  }, []);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  return (
    <div style={{ position: "relative" }}>
      {label ? (
        <div style={{ display: "grid", gap: 2 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#374151" }}>
              {label}
            </div>
            {Array.isArray(details) && details.length > 0 && (
              <span
                ref={iconRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#9ca3af",
                  cursor: "pointer",
                  marginTop: 1,
                }}
              >
                <InfoOutlinedIcon
                  className="info-icon-result-cs"
                  sx={{ fontSize: 16 }}
                />
              </span>
            )}
          </div>
          <div style={{ minWidth: 0 }}>{primary}</div>
        </div>
      ) : (
        <div
          style={{ display: "inline-flex", alignItems: "flex-start", gap: 3 }}
        >
          <div style={{ minWidth: 0 }}>{primary}</div>
          {Array.isArray(details) && details.length > 0 && (
            <span
              ref={iconRef}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#9ca3af",
                cursor: "pointer",
                marginTop: 1,
              }}
            >
              <InfoOutlinedIcon
                className="info-icon-result-cs"
                sx={{ fontSize: 16 }}
              />
            </span>
          )}
        </div>
      )}

      {pos &&
        createPortal(
          <div
            onMouseEnter={handleCardMouseEnter}
            onMouseLeave={handleCardMouseLeave}
            style={{
              position: "fixed",
              zIndex: 9999,
              width: HOVER_CARD_WIDTH,
              maxHeight: pos?.maxHeight || HOVER_CARD_MAX_HEIGHT,
              borderRadius: 12,
              border: "1px solid #e5e7eb",
              background: "#fff",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.12)",
              overflow: "hidden",
              pointerEvents: "auto",
              top: pos.y,
              left: pos.x,
            }}
          >
            <div style={{ background: "#cc1f1f", padding: "8px 12px" }}>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#fff",
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                More Details
              </span>
            </div>
            <div
              style={{
                maxHeight: (pos?.maxHeight || HOVER_CARD_MAX_HEIGHT) - 44,
                overflowY: "auto",
              }}
            >
              {details.map((d, idx) => (
                <div
                  key={d.label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 12,
                    padding: "8px 12px",
                    borderTop: idx === 0 ? "none" : "1px solid #f3f4f6",
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#9ca3af",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {d.label}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#3a6fa5",
                      textAlign: "right",
                    }}
                  >
                    {d.value}
                  </span>
                </div>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

const firstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const normalizeStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase();

const normalizeSource = (source) =>
  String(source || "")
    .trim()
    .toLowerCase();

const isReviewedStatus = (status) => {
  const normalized = normalizeStatus(status);
  return normalized === "reviewed" || normalized === "reviewd";
};

const isTruthy = (value) => {
  if (typeof value === "boolean") {
    return value;
  }
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return normalized === "true" || normalized === "1" || normalized === "yes";
};

const toNullableBoolean = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  return isTruthy(value);
};

const getAllDCRLinks = (result) => {
  let baseAppUrl = CONFIG.MSAL_REDIRECT_URI;
  const env = process.env.REACT_APP_STAGE;
  if (env === "dev_2") {
    baseAppUrl = "https://ecs-dev.lilly.com";
  }

  const dcrSources = [
    ...(Array.isArray(result?.dcr_ids) ? result.dcr_ids : []),
    ...(Array.isArray(result?.results) ? result.results : []),
  ];

  if (dcrSources.length > 0) {
    return dcrSources
      .map((dcr) => {
        let link = null;
        switch (dcr?.vendor) {
          case "IQVIA_US":
          case "IQVIA":
            link = `${baseAppUrl}/track-iqviaDetail/${dcr?.change_request_id}`;
            break;
          case "VNDR-VN":
            link = `${baseAppUrl}/track-vnDetail/${dcr?.change_request_id}`;
            break;
          case "VNDR-VOD":
            link = `${baseAppUrl}/view-all/requests/${dcr?.change_request_id}/OPEN-DATA`;
            break;
          case "Lilly":
          case "EPH":
            link = `${baseAppUrl}/view-all/requests/${dcr?.change_request_id}/Lilly`;
            break;
          default:
            link = null;
        }

        return {
          id: dcr?.change_request_id,
          vendor: dcr?.vendor,
          link,
        };
      })
      .filter((dcr) => dcr.link !== null && dcr.id)
      .filter(
        (dcr, index, arr) =>
          arr.findIndex(
            (item) =>
              String(item.id || "") === String(dcr.id || "") &&
              String(item.vendor || "") === String(dcr.vendor || ""),
          ) === index,
      );
  }

  return [];
};

const parseDcrOutcomeResult = (payload, shellProfilePayload) => {
  const primaryPayload = payload && typeof payload === "object" ? payload : {};
  const fallbackPayload =
    shellProfilePayload && typeof shellProfilePayload === "object"
      ? shellProfilePayload
      : {};

  const entityType = firstValue(
    primaryPayload?.EntityType,
    primaryPayload?.entityType,
    primaryPayload?.entity_type,
    fallbackPayload?.EntityType,
    fallbackPayload?.entityType,
    fallbackPayload?.entity_type,
    "",
  );

  const hcpEnterpriseId = firstValue(
    primaryPayload?.HcpEnterpriseId,
    primaryPayload?.HCPEnterpriseId,
    primaryPayload?.hcpEnterpriseId,
    primaryPayload?.hcp_enterprise_id,
    fallbackPayload?.HcpEnterpriseId,
    fallbackPayload?.HCPEnterpriseId,
    fallbackPayload?.hcpEnterpriseId,
    fallbackPayload?.hcp_enterprise_id,
    "",
  );

  const hcpEntityId = firstValue(
    primaryPayload?.HcpEntityId,
    primaryPayload?.HCPEntityId,
    primaryPayload?.hcpEntityId,
    primaryPayload?.hcp_entity_id,
    fallbackPayload?.HcpEntityId,
    fallbackPayload?.HCPEntityId,
    fallbackPayload?.hcpEntityId,
    fallbackPayload?.hcp_entity_id,
    "",
  );

  const hcoEnterpriseId = firstValue(
    primaryPayload?.HcoEnterpriseId,
    primaryPayload?.HCOEnterpriseId,
    primaryPayload?.hcoEnterpriseId,
    primaryPayload?.hco_enterprise_id,
    fallbackPayload?.HcoEnterpriseId,
    fallbackPayload?.HCOEnterpriseId,
    fallbackPayload?.hcoEnterpriseId,
    fallbackPayload?.hco_enterprise_id,
    "",
  );

  const hcoEntityId = firstValue(
    primaryPayload?.HcoEntityId,
    primaryPayload?.HCOEntityId,
    primaryPayload?.hcoEntityId,
    primaryPayload?.hco_entity_id,
    fallbackPayload?.HcoEntityId,
    fallbackPayload?.HCOEntityId,
    fallbackPayload?.hcoEntityId,
    fallbackPayload?.hco_entity_id,
    "",
  );

  const processedStatus = firstValue(
    primaryPayload?.processed_status,
    primaryPayload?.processedStatus,
    fallbackPayload?.processed_status,
    fallbackPayload?.processedStatus,
    "",
  );

  const statusRaw = firstValue(
    primaryPayload?.Status,
    primaryPayload?.status,
    fallbackPayload?.Status,
    fallbackPayload?.status,
    undefined,
  );
  const status =
    statusRaw === undefined || statusRaw === null || statusRaw === ""
      ? null
      : isTruthy(statusRaw);

  const hasAnyOutcomeValue =
    !!entityType ||
    !!hcpEnterpriseId ||
    !!hcpEntityId ||
    !!hcoEnterpriseId ||
    !!hcoEntityId ||
    !!processedStatus ||
    status !== null;

  if (!hasAnyOutcomeValue) {
    return null;
  }

  return {
    entityType,
    hcpEnterpriseId,
    hcpEntityId,
    hcoEnterpriseId,
    hcoEntityId,
    processedStatus,
    status,
  };
};

const parseDcrResult = (
  dcrResult,
  fallbackResultId,
  hasDcrResultKey = true,
  rootRow = {},
) => {
  const payload = dcrResult && typeof dcrResult === "object" ? dcrResult : null;
  const rowPayload = rootRow && typeof rootRow === "object" ? rootRow : {};
  const shellProfilePayload =
    rowPayload?.shell_profile_result &&
    typeof rowPayload.shell_profile_result === "object"
      ? rowPayload.shell_profile_result
      : rowPayload?.shellProfileResult &&
          typeof rowPayload.shellProfileResult === "object"
        ? rowPayload.shellProfileResult
        : payload?.shell_profile_result &&
            typeof payload.shell_profile_result === "object"
          ? payload.shell_profile_result
          : payload?.shellProfileResult &&
              typeof payload.shellProfileResult === "object"
            ? payload.shellProfileResult
            : null;
  const hasShellProfile =
    isTruthy(
      firstValue(
        rowPayload?.has_shell_profile,
        rowPayload?.hasShellProfile,
        payload?.has_shell_profile,
        payload?.hasShellProfile,
        false,
      ),
    ) || !!shellProfilePayload;
  const dcrOutcomeResult = parseDcrOutcomeResult(payload, shellProfilePayload);

  const dcrLinks = getAllDCRLinks(payload || {});
  const dcrLinkById = new Map(
    dcrLinks.map((item) => [String(item.id || ""), item.link]),
  );

  const dcrIdsList = Array.isArray(payload?.dcr_ids) ? payload.dcr_ids : [];
  const resultList = Array.isArray(payload?.results) ? payload.results : [];

  const dcrIdItems = dcrIdsList
    .map((item) => ({
      id: firstValue(
        item?.change_request_id,
        item?.changeRequestId,
        item?.id,
        "",
      ),
      vendor: firstValue(item?.vendor, item?.source, ""),
      status: item?.status,
    }))
    .filter((item) => item.id)
    .map((item) => ({
      id: String(item.id),
      vendor: item.vendor ? String(item.vendor) : "-",
      status:
        item.status === undefined || item.status === null || item.status === ""
          ? null
          : isTruthy(item.status),
      link: dcrLinkById.get(String(item.id)) || null,
      sourceType: "dcr_ids",
    }));

  const resultItems = resultList
    .map((item) => ({
      id: firstValue(
        item?.change_request_id,
        item?.changeRequestId,
        item?.id,
        "",
      ),
      vendor: firstValue(item?.vendor, item?.source, ""),
    }))
    .filter((item) => item.id)
    .map((item) => ({
      id: String(item.id),
      vendor: item.vendor ? String(item.vendor) : "-",
      link: dcrLinkById.get(String(item.id)) || null,
      sourceType: "results",
    }));

  const fallbackItems = (fallbackResultId ? [String(fallbackResultId)] : [])
    .filter(Boolean)
    .map((id) => ({
      id,
      vendor: "-",
      link: null,
      sourceType: "fallback",
    }));

  const dcrItems =
    hasDcrResultKey && (dcrIdItems.length > 0 || resultItems.length > 0)
      ? [...dcrIdItems, ...resultItems]
      : hasDcrResultKey
        ? fallbackItems
        : [];
  const hasMeaningfulDcrData =
    dcrItems.length > 0 ||
    !!dcrOutcomeResult ||
    !!firstValue(
      payload?.error,
      payload?.error_message,
      payload?.errorMessage,
      payload?.processed_status,
      payload?.processedStatus,
      payload?.validation_status,
      payload?.validationStatus,
      false,
    );

  const dcrIdsFromResults = dcrItems.map((item) => item.id).filter(Boolean);

  const hasErrorKey = payload?.error !== undefined && payload?.error !== null;
  const processedStatusValue = firstValue(
    payload?.processed_status,
    payload?.processedStatus,
    "",
  );
  const isProcessedStatusFailed =
    String(processedStatusValue).trim().toLowerCase() === "failed";
  const hasError = hasErrorKey
    ? isTruthy(payload?.error)
    : isProcessedStatusFailed;

  return {
    isMissing: !hasDcrResultKey || !hasMeaningfulDcrData,
    hasError,
    errorMessage: firstValue(
      payload?.error_message,
      payload?.errorMessage,
      !hasErrorKey && isProcessedStatusFailed
        ? "Failed to process the DCR."
        : "",
    ),
    errorType: firstValue(payload?.error_type, payload?.errorType, ""),
    processedStatus: processedStatusValue,
    validationStatus: firstValue(
      payload?.validation_status,
      payload?.validationStatus,
      "",
    ),
    hasShellProfile,
    shellProfileResult: shellProfilePayload
      ? parseDcrOutcomeResult({}, shellProfilePayload)
      : null,
    dcrOutcomeResult,
    dcrItems,
    dcrIdItems,
    resultItems,
    dcrIds: dcrIdsFromResults,
  };
};

const parseSubscriptionResult = (row) => {
  const shellProfileResult =
    row?.shell_profile_result && typeof row.shell_profile_result === "object"
      ? row.shell_profile_result
      : row?.shellProfileResult && typeof row.shellProfileResult === "object"
        ? row.shellProfileResult
        : {};

  const shellProfileStatusRaw = firstValue(
    row?.shell_profile_status,
    row?.shellProfileStatus,
    shellProfileResult?.shell_profile_status,
    shellProfileResult?.shellProfileStatus,
    shellProfileResult?.Status,
    shellProfileResult?.status,
    undefined,
  );

  const shellProfileStatus =
    shellProfileStatusRaw === undefined ||
    shellProfileStatusRaw === null ||
    shellProfileStatusRaw === ""
      ? null
      : isTruthy(shellProfileStatusRaw);

  const hasKey = (key) =>
    Object.prototype.hasOwnProperty.call(row || {}, key) ||
    Object.prototype.hasOwnProperty.call(shellProfileResult || {}, key);

  const hasAnyKey = (keys = []) => keys.some((key) => hasKey(key));

  const isEnabledByKey = (key) => {
    if (!hasKey(key)) {
      return false;
    }
    const value = firstValue(row?.[key], shellProfileResult?.[key], false);
    return isTruthy(value);
  };

  const isEnabledByKeys = (keys = []) => {
    if (!hasAnyKey(keys)) {
      return false;
    }
    const value = firstValue(
      ...keys.map((key) =>
        firstValue(row?.[key], shellProfileResult?.[key], undefined),
      ),
      false,
    );
    return isTruthy(value);
  };

  const normalizeErrors = () => {
    const errorValue = firstValue(
      shellProfileResult?.errors,
      shellProfileResult?.error,
      shellProfileResult?.error_message,
      shellProfileResult?.errorMessage,
      "",
    );

    if (Array.isArray(errorValue)) {
      return errorValue.filter(Boolean).map((item) => String(item));
    }
    if (errorValue && typeof errorValue === "object") {
      return Object.entries(errorValue).map(
        ([key, value]) => `${key}: ${String(value)}`,
      );
    }
    if (errorValue) {
      return [String(errorValue)];
    }
    return [];
  };

  const hcpSource = firstValue(
    row?.hcp_selected_source,
    row?.hcpSource,
    row?.hcp_source,
    row?.source,
    "",
  );
  const hcoSource = firstValue(
    row?.hco_selected_source,
    row?.hcoSource,
    row?.hco_source,
    row?.source,
    "",
  );
  const parentHcoSource = firstValue(
    row?.parent_hco_selected_source,
    row?.parentHcoSource,
    row?.parent_hco_source,
    row?.hco_selected_source,
    row?.hcoSource,
    row?.hco_source,
    row?.source,
    "",
  );

  const hcoData = {
    enterpriseId: firstValue(
      shellProfileResult?.HcoEnterpriseId,
      shellProfileResult?.HCOEnterpriseId,
      shellProfileResult?.hcoEnterpriseId,
      shellProfileResult?.hco_enterprise_id,
      "",
    ),
    entityId: firstValue(
      shellProfileResult?.HcoEntityId,
      shellProfileResult?.HCOEntityId,
      shellProfileResult?.hcoEntityId,
      shellProfileResult?.hco_entity_id,
      "",
    ),
    foundInEph: firstValue(
      shellProfileResult?.HcoOneKeyIdFoundInEPH,
      shellProfileResult?.HCOOneKeyIdFoundInEPH,
      shellProfileResult?.hcoOneKeyIdFoundInEPH,
      shellProfileResult?.hco_one_key_id_found_in_eph,
      false,
    ),
  };

  const hcpData = {
    enterpriseId: firstValue(
      shellProfileResult?.HcpEnterpriseId,
      shellProfileResult?.HCPEnterpriseId,
      shellProfileResult?.hcpEnterpriseId,
      shellProfileResult?.hcp_enterprise_id,
      "",
    ),
    entityId: firstValue(
      shellProfileResult?.HcpEntityId,
      shellProfileResult?.HCPEntityId,
      shellProfileResult?.hcpEntityId,
      shellProfileResult?.hcp_entity_id,
      "",
    ),
    foundInEph: firstValue(
      shellProfileResult?.HcpOneKeyIdFoundInEPH,
      shellProfileResult?.HCPOneKeyIdFoundInEPH,
      shellProfileResult?.hcpOneKeyIdFoundInEPH,
      shellProfileResult?.hcp_one_key_id_found_in_eph,
      false,
    ),
  };

  const parentHcoData = {
    enterpriseId: firstValue(
      shellProfileResult?.ParentHcoEnterpriseId,
      shellProfileResult?.ParentHCOEnterpriseId,
      shellProfileResult?.parentHcoEnterpriseId,
      shellProfileResult?.parent_hco_enterprise_id,
      "",
    ),
    entityId: firstValue(
      shellProfileResult?.ParentHcoEntityId,
      shellProfileResult?.ParentHCOEntityId,
      shellProfileResult?.parentHcoEntityId,
      shellProfileResult?.parent_hco_entity_id,
      "",
    ),
    foundInEph: firstValue(
      shellProfileResult?.ParentHcoOneKeyIdFoundInEPH,
      shellProfileResult?.ParentHCOOneKeyIdFoundInEPH,
      shellProfileResult?.parentHcoOneKeyIdFoundInEPH,
      shellProfileResult?.parent_hco_one_key_id_found_in_eph,
      false,
    ),
  };

  const entityDetails = [];

  if (
    isEnabledByKeys([
      "hcp_subscription_status",
      "hcpSubscriptionStatus",
      "HcpSubscriptionStatus",
      "HCPSubscriptionStatus",
    ])
  ) {
    entityDetails.push({
      key: "hcp",
      label: "HCP",
      source: hcpSource,
      ...hcpData,
    });
  }

  if (
    isEnabledByKeys([
      "hco_subscription_status",
      "hcoSubscriptionStatus",
      "HcoSubscriptionStatus",
      "HCOSubscriptionStatus",
    ])
  ) {
    entityDetails.push({
      key: "hco",
      label: "HCO",
      source: hcoSource,
      ...hcoData,
    });
  }

  if (
    isEnabledByKeys([
      "parent_hco_subscription_status",
      "parentHcoSubscriptionStatus",
      "ParentHcoSubscriptionStatus",
      "ParentHCOSubscriptionStatus",
    ])
  ) {
    entityDetails.push({
      key: "parent_hco",
      label: "Parent HCO",
      source: parentHcoSource,
      ...parentHcoData,
    });
  }

  const hasMeaningfulSubscriptionData =
    shellProfileStatus !== null ||
    entityDetails.length > 0 ||
    normalizeErrors().length > 0;

  return {
    isMissing: !hasMeaningfulSubscriptionData,
    shellProfileStatus,
    shellProfileStatusRaw,
    errors: normalizeErrors(),
    entityDetails,
    updatedAt: firstValue(
      row?.shell_profile_updated_at,
      row?.shellProfileUpdatedAt,
      shellProfileResult?.shell_profile_updated_at,
      shellProfileResult?.shellProfileUpdatedAt,
      "",
    ),
  };
};

const parseConsentResult = (row) => {
  const consentPayload =
    row?.consent_result && typeof row.consent_result === "object"
      ? row.consent_result
      : row?.consentResult && typeof row.consentResult === "object"
        ? row.consentResult
        : {};

  const consentUploadedRaw = firstValue(
    consentPayload?.consent_uploaded,
    consentPayload?.consentUploaded,
    undefined,
  );
  const hasConsentResultKey =
    Object.prototype.hasOwnProperty.call(row || {}, "consent_result") ||
    Object.prototype.hasOwnProperty.call(row || {}, "consentResult");
  const consentUploaded = toNullableBoolean(consentUploadedRaw);
  const consentUploadedAt = firstValue(
    consentPayload?.consent_uploaded_at,
    consentPayload?.consentUploadedAt,
    "",
  );
  const emailConsentStatus = toNullableBoolean(
    firstValue(
      consentPayload?.email_consent_status,
      consentPayload?.emailConsentStatus,
      undefined,
    ),
  );
  const phoneConsentStatus = toNullableBoolean(
    firstValue(
      consentPayload?.phone_consent_status,
      consentPayload?.phoneConsentStatus,
      undefined,
    ),
  );
  const emailConsentError = String(
    firstValue(
      consentPayload?.email_consent_error,
      consentPayload?.emailConsentError,
      "",
    ) || "",
  ).trim();
  const phoneConsentError = String(
    firstValue(
      consentPayload?.phone_consent_error,
      consentPayload?.phoneConsentError,
      "",
    ) || "",
  ).trim();

  const hasConsentUploadDetails =
    consentUploaded !== null || Boolean(consentUploadedAt);
  const hasConsentStatusDetails =
    emailConsentStatus !== null || phoneConsentStatus !== null;
  const hasConsentErrorDetails =
    Boolean(emailConsentError) || Boolean(phoneConsentError);
  const hasMeaningfulConsentData =
    hasConsentUploadDetails ||
    hasConsentStatusDetails ||
    hasConsentErrorDetails;

  return {
    isMissing: !hasConsentResultKey || !hasMeaningfulConsentData,
    consentUploaded,
    consentUploadedAt,
    emailConsentStatus,
    phoneConsentStatus,
    emailConsentError,
    phoneConsentError,
  };
};

const isDcrFailed = (row) => {
  const dcrResult = row?.dcrResult || {};
  if (dcrResult?.hasError) {
    return true;
  }

  const dcrIdItems = Array.isArray(dcrResult?.dcrIdItems)
    ? dcrResult.dcrIdItems
    : [];

  return dcrIdItems.some((item) => item?.status === false);
};

const isRecordInProgress = (row) =>
  String(row?.recordProcessStatus || "")
    .trim()
    .toUpperCase() === "IN_PROGRESS";

const isDcrInQueue = (row) =>
  String(row?.dcrResult?.processedStatus || "")
    .trim()
    .toUpperCase() === "IN_QUEUE";

const getDcrResultStatus = (row) => {
  if (!row?.isDcrApplicable) {
    return "not_applicable";
  }
  if (isRecordInProgress(row) && isDcrInQueue(row)) {
    return "pending";
  }
  if (row?.dcrResult?.isMissing) {
    return "pending";
  }
  if (isDcrFailed(row)) {
    return "failed";
  }
  return "success";
};

const isSubscriptionFailed = (row) => {
  const subscription = row?.subscriptionResult || {};
  const errors = Array.isArray(subscription?.errors) ? subscription.errors : [];
  return subscription?.shellProfileStatus === false || errors.length > 0;
};

const getSubscriptionResultStatus = (row) => {
  if (!row?.isSubscriptionApplicable) {
    return "not_applicable";
  }
  if (row?.subscriptionResult?.isMissing) {
    return "pending";
  }
  if (isSubscriptionFailed(row)) {
    return "failed";
  }
  return "success";
};

const isConsentFailed = (row) => {
  const consentResult = row?.consentResult || {};
  if (
    consentResult?.emailConsentStatus === false ||
    consentResult?.phoneConsentStatus === false ||
    consentResult?.emailConsentError ||
    consentResult?.phoneConsentError
  ) {
    return true;
  }

  const consent = String(row?.consentStatus || "")
    .trim()
    .toLowerCase();
  if (!consent) {
    return false;
  }

  return (
    consent.includes("fail") ||
    consent.includes("error") ||
    consent.includes("reject")
  );
};

const getConsentResultStatus = (row) => {
  if (!row?.isConsentApplicable) {
    return "not_applicable";
  }
  if (row?.consentResult?.isMissing) {
    return "pending";
  }
  if (isConsentFailed(row)) {
    return "failed";
  }
  return "success";
};

const buildApplicabilityItems = (row) => [
  {
    key: "dcr",
    label: "DCR",
    status: getDcrResultStatus(row),
  },
  {
    key: "subscription",
    label: "Subscription",
    status: getSubscriptionResultStatus(row),
  },
  {
    key: "consent",
    label: "Consent",
    status: getConsentResultStatus(row),
  },
];

const getResponseRows = (responseData) => {
  if (Array.isArray(responseData)) {
    return responseData;
  }
  if (Array.isArray(responseData?.records)) {
    return responseData.records;
  }
  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }
  if (Array.isArray(responseData?.results)) {
    return responseData.results;
  }
  if (Array.isArray(responseData?.items)) {
    return responseData.items;
  }
  return [];
};

const getSelectionIdBySource = (selectedEntity, source) => {
  if (!selectedEntity || typeof selectedEntity !== "object") {
    return "";
  }

  const normalizedSource = normalizeSource(source);
  if (normalizedSource === "eph") {
    const inferredType = String(
      firstValue(
        selectedEntity?.type,
        selectedEntity?.entityType,
        selectedEntity?.individual?.type,
        "",
      ),
    )
      .trim()
      .toUpperCase();
    const isHcp =
      inferredType === "HCP" ||
      (selectedEntity.HCPUniqueId && !selectedEntity.HCOUniqueId);
    const isHco =
      inferredType === "HCO" ||
      inferredType === "WORKPLACE" ||
      (selectedEntity.HCOUniqueId && !selectedEntity.HCPUniqueId);

    if (isHcp) {
      return (
        firstValue(
          selectedEntity.HCPUniqueId,
          selectedEntity.HCPReferenceId,
          selectedEntity.EntityId,
          selectedEntity.HCOUniqueId,
          selectedEntity.HCOReferenceId,
        ) || ""
      );
    }

    if (isHco) {
      return (
        firstValue(
          selectedEntity.HCOUniqueId,
          selectedEntity.HCOReferenceId,
          selectedEntity.EntityId,
          selectedEntity.HCPUniqueId,
          selectedEntity.HCPReferenceId,
        ) || ""
      );
    }

    return (
      firstValue(
        selectedEntity.HCPUniqueId,
        selectedEntity.HCOUniqueId,
        selectedEntity.EntityId,
        selectedEntity.HCPReferenceId,
        selectedEntity.HCOReferenceId,
      ) || ""
    );
  }

  return (
    firstValue(
      selectedEntity?.entityId,
      selectedEntity?.entity?.vid__v,
      selectedEntity?.entity?.veevaid__v,
      selectedEntity?.individual?.individualEid,
      selectedEntity.onekeyEid,
      selectedEntity.onekeyid,
      selectedEntity?.activity?.activityEid,
      selectedEntity.HCPReferenceId,
      selectedEntity.HCOReferenceId,
      selectedEntity.EntityId,
    ) || ""
  );
};

const formatInputFieldLabel = (fieldKey = "") => {
  const titleCase = String(fieldKey)
    .replace(/_/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

  return titleCase
    .replace(/\bId\b/g, "ID")
    .replace(/\bHco\b/g, "HCO")
    .replace(/\bHcp\b/g, "HCP")
    .replace(/\bRel\b/g, "REL")
    .replace(/\bCda\b/g, "CDA");
};

const buildInputEntityFields = (entityType, entity) => {
  if (!entity || typeof entity !== "object") {
    return [];
  }

  const fields = [
    {
      label: entityType === "HCP" ? "Enterprise ID" : "Enterprise ID",
      value: firstValue(
        entity.enterprise_id,
        entity.EntityId,
        entity.entity_id,
        entity.HCPUniqueId,
        entity.HCOUniqueId,
        entity.HCPReferenceId,
        entity.HCOReferenceId,
        entity.onekeyEid,
        entity.onekeyid,
      ),
    },
    {
      label: "First Name",
      value: firstValue(entity.first_name, entity.firstName),
    },
    {
      label: "Last Name",
      value: firstValue(entity.last_name, entity.lastName),
    },
    {
      label: "Middle Name",
      value: firstValue(entity.middle_name, entity.middleName),
    },
    {
      label: "Name",
      value: firstValue(entity.name, entity.hco_name),
    },
    {
      label: "Identifier Type",
      value: firstValue(entity.identifier_type, entity.identifier_1_type),
    },
    {
      label: "Identifier ID",
      value: firstValue(entity.identifier_id, entity.identifier_1_id),
    },
    {
      label: "Country",
      value: firstValue(
        entity.country_code,
        entity.hco_adrs_country,
      ),
    },
    {
      label: "Address",
      value: firstValue(entity.hcp_adrs_line_1, entity.hco_adrs_line_1),
    },
    {
      label: "City",
      value: firstValue(entity.hcp_adrs_city, entity.hco_adrs_city),
    },
    {
      label: "Zip",
      value: firstValue(entity.hcp_adrs_zip, entity.hco_adrs_zip),
    },
    { label: "Email", value: entity.email },
  ];

  const normalizedEntityType = String(entityType || "")
    .trim()
    .toUpperCase();

  // For parent HCO inputs, include every rel_* key from payload so no relation field is missed.
  const relFields =
    normalizedEntityType === "PARENT_HCO"
      ? Object.entries(entity)
          .filter(
            ([key, value]) =>
              /^rel_/i.test(String(key || "")) &&
              value !== undefined &&
              value !== null &&
              value !== "",
          )
          .map(([key, value]) => ({
            label: formatInputFieldLabel(key),
            value,
          }))
      : [];

  return [...fields, ...relFields].filter(
    (f) => f.value !== undefined && f.value !== null && f.value !== "",
  );
};

const splitVisibleFields = (fields = [], visibleCount = 3) => {
  const visibleFields = fields.slice(0, visibleCount);
  const hiddenFields = fields.slice(visibleCount);
  return { visibleFields, hiddenFields };
};

const mapSelectionLabel = (entity, fallbackLabel) => {
  if (fallbackLabel === "Parent HCO") {
    return "Parent HCO";
  }

  const type = String(entity?.type || entity?.entityType || "").toUpperCase();
  if (type === "HCP") {
    return "HCP";
  }
  if (type === "HCO") {
    return "HCO";
  }
  if (type === "WORKPLACE") {
    return fallbackLabel;
  }
  return fallbackLabel;
};

const renderFieldLines = (fields, visibleCount = 3) => {
  const { visibleFields, hiddenFields } = splitVisibleFields(
    fields,
    visibleCount,
  );
  const hoverDetails = [
    ...hiddenFields,
    ...visibleFields.flatMap((field) =>
      Array.isArray(field.details)
        ? field.details.map((detail) => ({
            ...detail,
            label:
              field.label === "Enterprise ID" || field.label === "OneKey ID"
                ? detail.label
                : `${field.label} - ${detail.label}`,
          }))
        : [],
    ),
  ];
  return {
    primary: (
      <div style={{ display: "grid", gap: 2 }}>
        {visibleFields.map((field) => (
          <div key={field.label} style={{ fontSize: 12, color: "#6b7280" }}>
            {field.label}:{" "}
            <span style={{ color: "#1f2937", fontWeight: 500 }}>
              {field.value}
            </span>
          </div>
        ))}
      </div>
    ),
    details: hoverDetails,
  };
};

const formatListValue = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(", ");
  }
  return value;
};

const formatDateOnly = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) {
    return "-";
  }

  const isoLikeMatch = rawValue.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoLikeMatch) {
    return isoLikeMatch[1];
  }

  const parsedDate = new Date(rawValue);
  if (!Number.isNaN(parsedDate.getTime())) {
    const yyyy = parsedDate.getUTCFullYear();
    const mm = String(parsedDate.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(parsedDate.getUTCDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  return rawValue;
};

const parseDateMillis = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) {
    return null;
  }

  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate.getTime();
};

const formatDateTimeValue = (value) => {
  const timestamp = parseDateMillis(value);
  if (!timestamp) {
    return "-";
  }

  return new Date(timestamp)
    .toISOString()
    .replace("T", " ")
    .replace(/\.\d{3}Z$/, "");
};

const mapSourceLabel = (source) => {
  const normalized = normalizeSource(source);
  if (normalized === "vendor") {
    return "IQVIA";
  }
  if (normalized === "eph") {
    return "EPH";
  }
  if (normalized === "vod") {
    return "VOD";
  }
  return normalized ? normalized.toUpperCase() : "-";
};

const formatSelectionHeading = (label, source) => {
  const displayLabel = label || "Selection";
  const sourceLabel = mapSourceLabel(source);
  if (!sourceLabel || sourceLabel === "-") {
    return displayLabel;
  }
  return `${displayLabel}(${sourceLabel})`;
};

const getNoSelectionItems = (row) => {
  const items = [];

  if (row?.isHcpSelected === false) {
    items.push({
      key: "hcp",
      label: "HCP: No Selection",
      textColor: "#9a3412",
      background: "#ffedd5",
      borderColor: "#fed7aa",
    });
  }

  if (row?.isHcoSelected === false) {
    items.push({
      key: "hco",
      label: "HCO: No Selection",
      textColor: "#1d4ed8",
      background: "#dbeafe",
      borderColor: "#bfdbfe",
    });
  }

  if (row?.isParentHcoSelected === false) {
    items.push({
      key: "parent_hco",
      label: "Parent HCO: No Selection",
      textColor: "#166534",
      background: "#dcfce7",
      borderColor: "#bbf7d0",
    });
  }

  return items;
};

const appendSearchToken = (tokens, value) => {
  if (value === undefined || value === null) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => appendSearchToken(tokens, item));
    return;
  }

  if (typeof value === "object") {
    Object.values(value).forEach((item) => appendSearchToken(tokens, item));
    return;
  }

  const normalized = String(value).trim().toLowerCase();
  if (normalized) {
    tokens.push(normalized);
  }
};

const appendFieldSearchTokens = (tokens, fields = []) => {
  (fields || []).forEach((field) => {
    appendSearchToken(tokens, field?.label);
    appendSearchToken(tokens, field?.value);

    if (Array.isArray(field?.details)) {
      field.details.forEach((detail) => {
        appendSearchToken(tokens, detail?.label);
        appendSearchToken(tokens, detail?.value);
      });
    }
  });
};

const buildResultsRowSearchText = (row) => {
  const tokens = [];

  // Column: Input HCP/HCO
  appendFieldSearchTokens(tokens, [
    ...(row?.inputHcpFields || []),
    ...(row?.inputHcoFields || []),
  ]);

  // Column: Input Parent HCO
  appendFieldSearchTokens(tokens, row?.inputParentHcoFields || []);

  // Column: Selection From Search Results
  if (row?.selectionPrimary) {
    appendSearchToken(
      tokens,
      formatSelectionHeading(
        row.selectionPrimary?.label || "Selection",
        row.selectionPrimary?.source,
      ),
    );
    appendSearchToken(tokens, row.selectionPrimary?.idType);
    appendSearchToken(tokens, row.selectionPrimary?.id);
    appendFieldSearchTokens(tokens, row.selectionPrimary?.fields || []);
  }

  if (row?.selectionParent) {
    appendSearchToken(
      tokens,
      formatSelectionHeading(
        row.selectionParent?.label || "Parent Selection",
        row.selectionParent?.source,
      ),
    );
    appendSearchToken(tokens, row.selectionParent?.idType);
    appendSearchToken(tokens, row.selectionParent?.id);
    appendFieldSearchTokens(tokens, row.selectionParent?.fields || []);
  }

  getNoSelectionItems(row).forEach((item) => {
    appendSearchToken(tokens, item?.label);
  });

  appendSearchToken(tokens, row?.resultId);

  return tokens.join(" ");
};

const buildResultsRowSearchTokens = (row) => {
  const fields = [
    ...(row?.inputHcpFields || []),
    ...(row?.inputHcoFields || []),
    ...(row?.inputParentHcoFields || []),
    ...(row?.selectionPrimary?.fields || []),
    ...(row?.selectionParent?.fields || []),
  ];
  const values = fields.flatMap((field) => [
    field?.value,
    ...(field?.details || []).map((detail) => detail?.value),
  ]);

  values.push(
    row?.selectionPrimary?.id,
    row?.selectionParent?.id,
    row?.resultId,
    ...getNoSelectionItems(row).map((item) => item.label),
  );

  return values.flatMap((value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .split(/[\s,;:|/\\()[\]{}]+/)
      .filter(Boolean),
  );
};

const createEPHLink = (entityId) => {
  if (entityId === "-" || !entityId) {
    return null;
  }

  return process.env.REACT_APP_STAGE === "prod"
    ? `https://361.reltio.com/nui/N6QdWQ05wkTZjLA/profile?entityUri=entities%2F${entityId}`
    : `https://test.reltio.com/nui/mNNfGtHg2wVTZ1B/profile?entityUri=entities%2F${entityId}`;
};

const getAddressParts = (entity) => {
  const openDataEntity = entity?.entity;
  const openDataPrimaryAddress =
    Array.isArray(openDataEntity?.addresses__v) &&
    openDataEntity.addresses__v.length > 0
      ? openDataEntity.addresses__v.find(
          (address) =>
            String(address?.primary_cda__v || "").toUpperCase() === "Y",
        ) ||
        openDataEntity.addresses__v.find(
          (address) => Number(address?.address_ordinal__v) === 1,
        ) ||
        openDataEntity.addresses__v[0]
      : null;
  const profile = entity?.individual || entity?.hcp || entity?.HCP || entity;
  const workplace =
    entity?.workplace || entity?.parent_hco || entity?.parentHco || {};
  const primaryAddress = workplace?.workplaceAddresses?.["P,1"]?.address || {};
  const postal = primaryAddress?.postalTownReference || {};

  const line1 = firstValue(
    openDataPrimaryAddress?.formatted_address__v,
    openDataPrimaryAddress?.address_line_1__v,
    openDataPrimaryAddress?.street_address_1__v,
    openDataEntity?.street_address_1__v,
    entity?.address_line_1,
    entity?.addressLine1,
    entity?.hcp_adrs_line_1,
    entity?.hco_adrs_line_1,
    profile?.address_line_1,
    profile?.addressLine1,
    primaryAddress?.addressLine1,
    primaryAddress?.addressShortLabel,
    primaryAddress?.addressLongLabel,
    entity?.Address,
    entity?.address,
  );
  const line2 = firstValue(
    openDataPrimaryAddress?.address_line_2__v,
    entity?.address_line_2,
    entity?.addressLine2,
    entity?.hcp_adrs_line_2,
    entity?.hco_adrs_line_2,
    profile?.address_line_2,
    profile?.addressLine2,
    primaryAddress?.addressLine2,
  );
  const city = firstValue(
    openDataPrimaryAddress?.city_cda__v,
    openDataEntity?.city_cda__v,
    entity?.city,
    entity?.hcp_adrs_city,
    entity?.hco_adrs_city,
    postal?.villageLabel,
  );
  const state = firstValue(
    openDataPrimaryAddress?.state_cda__v,
    openDataEntity?.state_cda__v,
    entity?.state,
    entity?.state_province,
    entity?.hcp_adrs_state,
    entity?.hco_adrs_state,
    postal?.regionLabel,
  );
  const zip = firstValue(
    openDataPrimaryAddress?.postal_code__v,
    openDataPrimaryAddress?.postal_code_cda__v,
    openDataEntity?.postal_code_cda__v,
    entity?.zip,
    entity?.zip_code,
    entity?.postal_code,
    entity?.hcp_adrs_zip,
    entity?.hco_adrs_zip,
    primaryAddress?.zipCode,
  );
  const country = firstValue(
    openDataPrimaryAddress?.country__v,
    openDataEntity?.primary_country__v,
    openDataEntity?.country_cda__v,
    entity?.country,
    entity?.country_code,
    entity?.CountryCode,
    entity?.hcp_adrs_country,
    entity?.hco_adrs_country,
    postal?.countryLabel,
  );

  return {
    line1,
    line2,
    city,
    state,
    zip,
    country,
  };
};

const getPrimarySpecialty = (entity) =>
  firstValue(
    entity?.entity?.specialty_1__v,
    entity?.entity?.spec_1_local__v,
    entity?.primary_specialty,
    entity?.primarySpecialty,
    entity?.["individual.speciality1"],
    entity?.speciality1,
    entity?.specialty1,
    entity?.PrimarySpecialty,
    entity?.speciality,
    entity?.specialty,
    entity?.Specialities?.[0]?.value,
    entity?.Specialities?.[0],
    entity?.individual?.primary_specialty,
    entity?.individual?.primarySpecialty,
    entity?.individual?.speciality1,
    entity?.individual?.specialty1,
    entity?.individual?.Specialities?.[0]?.value,
    entity?.individual?.Specialities?.[0],
  );

const getSelectionIdLabelBySource = (selectedEntity, source) => {
  const normalizedSource = normalizeSource(source);
  if (normalizedSource === "eph") {
    return "Enterprise ID";
  }

  const hasOpenDataEntity =
    !!selectedEntity?.entity && typeof selectedEntity.entity === "object";
  return hasOpenDataEntity ? "Entity ID" : "OneKey ID";
};

const buildSelectionFields = (entity, source, fallbackLabel) => {
  if (!entity || typeof entity !== "object") {
    return [];
  }

  const normalizedSource = normalizeSource(source);
  const isEph = normalizedSource === "eph";
  const idLabel = getSelectionIdLabelBySource(entity, source);
  const vendorEntity =
    entity?.entity && typeof entity.entity === "object" ? entity.entity : null;

  const profile =
    entity?.individual || entity?.hcp || entity?.HCP || vendorEntity || entity;
  const rawType = String(
    firstValue(
      entity?.type,
      entity?.entityType,
      vendorEntity?.entityType,
      profile?.type,
      fallbackLabel,
    ) || "",
  )
    .trim()
    .toUpperCase();
  const isHcoLike =
    rawType === "HCO" ||
    rawType === "WORKPLACE" ||
    String(fallbackLabel || "")
      .toUpperCase()
      .includes("HCO");

  const identityId = isEph
    ? firstValue(
        entity.HCPUniqueId,
        entity.HCOUniqueId,
        entity.HCPReferenceId,
        entity.HCOReferenceId,
      )
    : firstValue(
        entity?.entityId,
        vendorEntity?.vid__v,
        vendorEntity?.veevaid__v,
        entity?.individual?.individualEid,
        entity.onekeyEid,
        entity.onekeyid,
        entity?.activity?.activityEid,
        entity.HCPReferenceId,
        entity.HCOReferenceId,
        entity.EntityId,
      );

  const displayName = firstValue(
    vendorEntity?.full_name__v,
    vendorEntity?.formatted_name__v,
    vendorEntity?.corporate_name__v,
    vendorEntity?.hco_name_cda__v,
    entity.Name,
    entity.name,
    profile?.Name,
    profile?.name,
    entity.hco_name,
  );

  const firstName = firstValue(
    vendorEntity?.first_name__v,
    profile?.FirstName,
    profile?.firstName,
    entity.FirstName,
    entity.first_name,
    entity.firstName,
    entity?.individual?.firstName,
  );
  const lastName = firstValue(
    vendorEntity?.last_name__v,
    profile?.LastName,
    profile?.lastName,
    entity.LastName,
    entity.last_name,
    entity.lastName,
    entity?.individual?.lastName,
  );
  const primarySpecialty = formatListValue(getPrimarySpecialty(entity));
  const address = getAddressParts(entity);
  const classificationCode = firstValue(
    vendorEntity?.hcp_type__v,
    vendorEntity?.hco_type__v,
    entity?.ClassificationCode,
    entity?.classification_code,
    entity?.classification,
    profile?.ClassificationCode,
    profile?.classification_code,
    profile?.classification,
    entity?.activity?.classificationCode,
    entity?.activity?.classification_code,
  );
  const activeFlag = firstValue(
    vendorEntity?.status_cda__v,
    vendorEntity?.hcp_status__v,
    vendorEntity?.hco_status__v,
    entity?.ActiveFlag,
    entity?.active_flag,
    entity?.activeFlag,
    profile?.ActiveFlag,
    profile?.active_flag,
    entity?.activity?.activeFlag,
    entity?.activity?.statusLabel,
    entity?.statusLabel,
  );
  const entityIdValue = firstValue(
    entity?.entityId,
    vendorEntity?.vid__v,
    vendorEntity?.veevaid__v,
    entity?.EntityId,
    entity?.entity_id,
    profile?.EntityId,
    profile?.entity_id,
    entity?.individual?.EntityId,
    entity?.individual?.entity_id,
  );

  const metadataHoverDetails = [
    { label: "Classification Code", value: classificationCode },
    { label: "Active Flag", value: activeFlag },
    { label: "EntityId", value: entityIdValue },
  ].filter(
    (field) =>
      field.value !== undefined && field.value !== null && field.value !== "",
  );

  const sharedAddressDetails = [
    { label: "Address Line 2", value: address.line2 },
    { label: "City", value: address.city },
    { label: "State", value: address.state },
    { label: "Zip", value: address.zip },
    { label: "Country", value: address.country },
  ].filter(
    (field) =>
      field.value !== undefined && field.value !== null && field.value !== "",
  );

  if (isHcoLike) {
    return [
      { label: idLabel, value: identityId, details: metadataHoverDetails },
      { label: "Name", value: displayName },
      { label: "Address", value: address.line1, details: sharedAddressDetails },
    ].filter(
      (field) =>
        field.value !== undefined && field.value !== null && field.value !== "",
    );
  }

  return [
    { label: idLabel, value: identityId, details: metadataHoverDetails },
    { label: "First Name", value: firstName },
    { label: "Last Name", value: lastName },
    { label: "Speciality", value: primarySpecialty },
    { label: "Address", value: address.line1, details: sharedAddressDetails },
  ].filter(
    (field) =>
      field.value !== undefined && field.value !== null && field.value !== "",
  );
};

const renderCompactSections = (sections = [], visibleCount = 3) => {
  const hiddenDetails = [];

  const primary = (
    <div style={{ display: "grid", gap: 8 }}>
      {sections.map((section) => {
        const { visibleFields, hiddenFields } = splitVisibleFields(
          section.fields || [],
          visibleCount,
        );
        hiddenDetails.push(
          ...hiddenFields.map((field) => ({
            ...field,
            label: `${section.title} - ${field.label}`,
          })),
        );

        return (
          <div key={section.title} style={{ display: "grid", gap: 2 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#374151" }}>
              {section.title}
            </div>
            {visibleFields.length === 0 ? (
              <span style={{ fontSize: 12, color: "#9ca3af" }}>-</span>
            ) : (
              visibleFields.map((field) => (
                <div
                  key={`${section.title}-${field.label}`}
                  style={{ fontSize: 12, color: "#6b7280" }}
                >
                  {field.label}:{" "}
                  <span style={{ color: "#1f2937", fontWeight: 500 }}>
                    {field.value}
                  </span>
                </div>
              ))
            )}
          </div>
        );
      })}
    </div>
  );

  return { primary, details: hiddenDetails };
};

const mapBulkConsentRow = (row, index) => {
  const input = row?.input || row?.input_record || {};
  const inputRowNumber = firstValue(
    row?.input_record?.input_row_number,
    row?.input?.input_row_number,
    input?.input_row_number,
  );
  const inputEntityType = String(
    firstValue(input?.entity_type, row?.entity_type, ""),
  )
    .trim()
    .toUpperCase();

  let hcpInput = input?.hcp || input?.HCP || null;
  let hcoInput = input?.hco || input?.HCO || null;
  let parentHcoInput = input?.parent_hco || input?.Parent_HCO || null;

  if (!hcpInput && !hcoInput && input && typeof input === "object") {
    if (inputEntityType === "HCP") {
      hcpInput = input;
    } else if (inputEntityType === "HCO") {
      hcoInput = input;
    }
  }

  if (!parentHcoInput) {
    const relEntries = Object.entries(input || {}).filter(
      ([key, value]) =>
        /^rel_/i.test(String(key || "")) &&
        value !== undefined &&
        value !== null &&
        value !== "",
    );

    if (relEntries.length > 0) {
      const relPayload = relEntries.reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});

      parentHcoInput = {
        ...relPayload,
        enterprise_id: firstValue(
          input?.rel_hco_enterprise_id,
          relPayload?.rel_hco_enterprise_id,
        ),
        name: firstValue(input?.rel_hco_name, relPayload?.rel_hco_name),
        country_code: firstValue(
          input?.rel_hco_country_code,
          relPayload?.rel_hco_country_code,
        ),
      };
    }
  }

  const selectedResult = row?.selected_result || row?.selectedResult || {};

  const selectedHcp = selectedResult?.hcp || null;
  const selectedHco = selectedResult?.hco || null;
  const selectedParentHco = selectedResult?.parent_hco || null;

  const primarySelectedEntity = selectedHcp || selectedHco || null;
  const primarySelectedSource = selectedHcp
    ? row?.hcp_selected_source
    : row?.hco_selected_source;
  const parentSelectedSource =
    row?.parent_hco_selected_source ||
    row?.hco_selected_source ||
    row?.hcp_selected_source;

  const status = firstValue(row?.status, row?.Status, "");
  const reviewed = isReviewedStatus(status);
  const flaggedNotFound = isTruthy(row?.is_flagged_as_not_found);
  const source = normalizeSource(
    firstValue(
      row?.source,
      row?.Source,
      row?.hco_source,
      row?.hcp_source,
      row?.parent_hco_source,
      "",
    ),
  );

  let category = "NO ACTION";
  if (!reviewed) {
    category = "NO ACTION";
  } else if (source === "not_found") {
    category = "NOT FOUND";
  } else if (source === "eph") {
    category = "EPH";
  } else if (source === "vendor") {
    category = "VENDOR";
  } else if (reviewed && flaggedNotFound) {
    category = "NOT FOUND";
  }

  const inputHcpFields = buildInputEntityFields("HCP", hcpInput);
  const inputHcoFields = buildInputEntityFields("HCO", hcoInput);
  const inputParentHcoFields = buildInputEntityFields(
    "PARENT_HCO",
    parentHcoInput,
  );

  const selectionPrimaryId = getSelectionIdBySource(
    primarySelectedEntity,
    primarySelectedSource,
  );
  const selectionParentId = getSelectionIdBySource(
    selectedParentHco,
    parentSelectedSource,
  );

  const selectionPrimaryType = getSelectionIdLabelBySource(
    primarySelectedEntity,
    primarySelectedSource,
  );
  const selectionParentType = getSelectionIdLabelBySource(
    selectedParentHco,
    parentSelectedSource,
  );

  const selectionPrimaryFields = buildSelectionFields(
    primarySelectedEntity,
    primarySelectedSource,
    selectedHcp ? "HCP" : "HCO",
  );

  const selectionParentFields = buildSelectionFields(
    selectedParentHco,
    parentSelectedSource,
    "Parent HCO",
  );

  const rawResultId = firstValue(
    row?.resultId,
    row?.result_id,
    row?.dcr_id,
    "",
  );
  const hasDcrResultKey =
    Object.prototype.hasOwnProperty.call(row || {}, "dcr_result") ||
    Object.prototype.hasOwnProperty.call(row || {}, "dce_result") ||
    Object.prototype.hasOwnProperty.call(row || {}, "dcrResult") ||
    Object.prototype.hasOwnProperty.call(row || {}, "dceResult");
  const dcrResult = parseDcrResult(
    firstValue(
      row?.dcr_result,
      row?.dce_result,
      row?.dcrResult,
      row?.dceResult,
      null,
    ),
    rawResultId,
    hasDcrResultKey,
    row,
  );
  const subscriptionResult = parseSubscriptionResult(row || {});
  const consentResult = parseConsentResult(row || {});

  return {
    id: firstValue(row?.pk, row?.id, `consent-${index + 1}`),
    csvRow: firstValue(inputRowNumber, row?.csv_row, row?.csvRow, index + 1),
    category,
    inputHcpFields,
    inputHcoFields,
    inputParentHcoFields,
    selectionPrimary: selectionPrimaryId
      ? {
          label: mapSelectionLabel(
            primarySelectedEntity,
            selectedHcp ? "HCP" : "HCO",
          ),
          idType: selectionPrimaryType,
          source: primarySelectedSource,
          id: selectionPrimaryId,
          fields: selectionPrimaryFields,
        }
      : null,
    selectionParent: selectionParentId
      ? {
          label: mapSelectionLabel(selectedParentHco, "Parent HCO"),
          idType: selectionParentType,
          source: parentSelectedSource,
          id: selectionParentId,
          fields: selectionParentFields,
        }
      : null,
    isHcpSelected: toNullableBoolean(
      firstValue(row?.is_hcp_selected, row?.isHcpSelected),
    ),
    isHcoSelected: toNullableBoolean(
      firstValue(row?.is_hco_selected, row?.isHcoSelected),
    ),
    isParentHcoSelected: toNullableBoolean(
      firstValue(row?.is_parent_hco_selected, row?.isParentHcoSelected),
    ),
    consentStatus: firstValue(
      row?.consentStatus,
      row?.consent_status,
      row?.consent_status_label,
      row?.is_consent_applicable !== undefined
        ? isTruthy(row?.is_consent_applicable)
          ? "Applicable"
          : "Not Applicable"
        : undefined,
      row?.status,
      "Applicable",
    ),
    isConsentApplicable: isTruthy(
      firstValue(row?.is_consent_applicable, row?.isConsentApplicable, false),
    ),
    isDcrApplicable: isTruthy(
      firstValue(row?.is_dcr_applicable, row?.isDcrApplicable, false),
    ),
    isSubscriptionApplicable: isTruthy(
      firstValue(
        row?.is_subscription_applicable,
        row?.isSubscriptionApplicable,
        false,
      ),
    ),
    result: firstValue(
      row?.result,
      row?.result_label,
      row?.resultText,
      row?.batch_status,
      "",
    ),
    resultId: firstValue(rawResultId, ""),
    dcrResult,
    subscriptionResult,
    consentResult,
    updatedDate: firstValue(
      row?.updatedDate,
      row?.updated_at,
      row?.updated_date,
      row?.update_date,
      row?.result_updated_date,
      "",
    ),
    recordProcessStatus: String(
      firstValue(row?.record_process_status, row?.recordProcessStatus, ""),
    )
      .trim()
      .toUpperCase(),
  };
};

const mapLegacyRow = (row, category, index) => {
  const inputHcpFields = [
    { label: "Enterprise ID", value: row?.hcpId },
    { label: "First Name", value: row?.hcpFirstName },
    { label: "Last Name", value: row?.hcpLastName },
  ].filter((f) => f.value);

  const inputParentHcoFields = [
    { label: "Enterprise ID", value: row?.parentId },
    { label: "Name", value: row?.parentName },
    { label: "Address", value: row?.parentAddress },
  ].filter((f) => f.value);

  const rawResultId = firstValue(
    row?.resultId,
    row?.result_id,
    row?.dcr_id,
    "",
  );
  const hasDcrResultKey =
    Object.prototype.hasOwnProperty.call(row || {}, "dcr_result") ||
    Object.prototype.hasOwnProperty.call(row || {}, "dce_result") ||
    Object.prototype.hasOwnProperty.call(row || {}, "dcrResult") ||
    Object.prototype.hasOwnProperty.call(row || {}, "dceResult");
  const dcrResult = parseDcrResult(
    firstValue(
      row?.dcr_result,
      row?.dce_result,
      row?.dcrResult,
      row?.dceResult,
      null,
    ),
    rawResultId,
    hasDcrResultKey,
    row,
  );
  const subscriptionResult = parseSubscriptionResult(row || {});
  const consentResult = parseConsentResult(row || {});

  return {
    id: row?.id || `legacy-${category}-${index + 1}`,
    csvRow: firstValue(row?.csvRow, index + 1),
    category,
    inputHcpFields,
    inputHcoFields: [],
    inputParentHcoFields,
    selectionPrimary:
      row?.selHcpEph || row?.selHcpIqvia
        ? {
            label: "HCP/HCO",
            idType: row?.selHcpEph ? "Enterprise ID" : "OneKeyID",
            source: row?.selHcpEph ? "eph" : "vendor",
            id: row?.selHcpEph || row?.selHcpIqvia,
          }
        : null,
    selectionParent:
      row?.selParentEph || row?.selParentIqvia
        ? {
            label: "Parent HCO",
            idType: row?.selParentEph ? "Enterprise ID" : "OneKeyID",
            source: row?.selParentEph ? "eph" : "vendor",
            id: row?.selParentEph || row?.selParentIqvia,
          }
        : null,
    isHcpSelected: null,
    isHcoSelected: null,
    isParentHcoSelected: null,
    consentStatus: firstValue(row?.consentStatus, "Applicable"),
    isConsentApplicable: isTruthy(
      firstValue(row?.is_consent_applicable, row?.isConsentApplicable, false),
    ),
    isDcrApplicable: isTruthy(
      firstValue(row?.is_dcr_applicable, row?.isDcrApplicable, false),
    ),
    isSubscriptionApplicable: isTruthy(
      firstValue(
        row?.is_subscription_applicable,
        row?.isSubscriptionApplicable,
        false,
      ),
    ),
    result: firstValue(row?.result, ""),
    resultId: firstValue(row?.resultId, ""),
    dcrResult,
    subscriptionResult,
    consentResult,
    updatedDate: firstValue(row?.updatedDate, ""),
    recordProcessStatus: String(
      firstValue(row?.record_process_status, row?.recordProcessStatus, ""),
    )
      .trim()
      .toUpperCase(),
  };
};

export default function Results({
  ephRecords = [],
  vendorRecords = [],
  notFoundRecords = [],
  onBack,
}) {
  const history = useHistory();
  const [showHelpModal, setShowHelpModal] = useState(false);
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("EPH");
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [apiRows, setApiRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState("");
  const [resultMetaData, setResultMetaData] = useState({
    uploadId: "",
    recordCreatedDate: "",
    recordUpdatedDate: "",
    lastUpdatedDate: "",
  });
  const [isAllDataComplete, setIsAllDataComplete] = useState(false);
  const [hasPollingWindowCompleted, setHasPollingWindowCompleted] =
    useState(false);
  const [isManualRefreshLoading, setIsManualRefreshLoading] = useState(false);
  const [isAutoPollingActive, setIsAutoPollingActive] = useState(false);
  const [detailsPopupState, setDetailsPopupState] = useState({
    anchorEl: null,
    row: null,
  });
  const [supportedVendorCountries, setSupportedVendorCountries] = useState(
    new Set(),
  );

  const uploadId =
    location?.state?.uploadId || sessionStorage.getItem("uploadId") || "";
  const userCountry = sessionStorage.getItem("userSelectedCountry");
  const pollingTimeoutRef = useRef(null);
  const manualRefreshTimeoutRef = useRef(null);
  const manualRefreshEndTimeoutRef = useRef(null);
  const autoPollingEndTimeoutRef = useRef(null);
  const autoPollingExpiredRef = useRef(false);
  const manualRefreshExpiredRef = useRef(false);
  const pollingWindowStartRef = useRef(null);
  const currentOrderRef = useRef(1);
  const cycleStatusByIdRef = useRef(new Map());
  const allDataCompleteRef = useRef(false);

  const handleOpenDetailsPopup = (event, row) => {
    setDetailsPopupState({ anchorEl: event.currentTarget, row });
  };

  const handleCloseDetailsPopup = () => {
    setDetailsPopupState({ anchorEl: null, row: null });
  };

  const isDetailsPopupOpen = Boolean(detailsPopupState.anchorEl);
  const detailsPopupRow = detailsPopupState.row;
  const detailsPopupDcrResult = detailsPopupRow?.dcrResult || {};
  const detailsPopupSubscriptionResult =
    detailsPopupRow?.subscriptionResult || {};
  const subscriptionShellProfileStatus =
    detailsPopupSubscriptionResult?.shellProfileStatus;
  const isSubscriptionShellProfileFailed =
    subscriptionShellProfileStatus === false;
  const subscriptionPopupErrors = Array.isArray(
    detailsPopupSubscriptionResult?.errors,
  )
    ? detailsPopupSubscriptionResult.errors
    : detailsPopupSubscriptionResult?.errors
      ? [String(detailsPopupSubscriptionResult.errors)]
      : [];
  const hasSubscriptionFailureDetails =
    isSubscriptionShellProfileFailed || subscriptionPopupErrors.length > 0;
  const subscriptionPopupEntityDetails =
    detailsPopupSubscriptionResult?.entityDetails || [];
  const isDcrPopupPending =
    isRecordInProgress(detailsPopupRow) && isDcrInQueue(detailsPopupRow);
  const consentPopupResult = detailsPopupRow?.consentResult || {};
  const showDcrDetailsSection = detailsPopupRow?.isDcrApplicable === true;
  const showSubscriptionDetailsSection =
    detailsPopupRow?.isSubscriptionApplicable === true;
  const showConsentDetailsSection =
    detailsPopupRow?.isConsentApplicable === true;
  const hasConsentPopupDetails =
    consentPopupResult.consentUploaded !== null ||
    Boolean(consentPopupResult.consentUploadedAt) ||
    consentPopupResult.emailConsentStatus !== null ||
    consentPopupResult.phoneConsentStatus !== null ||
    Boolean(consentPopupResult.emailConsentError) ||
    Boolean(consentPopupResult.phoneConsentError);

  useEffect(() => {
    let isMounted = true;

    const fetchAndFilterSupportedCountries = async () => {
      try {
        const response = await api.getSelectedDCRCountry();
        const allCountries = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        const supported = new Set();
        allCountries.forEach((country) => {
          if (!country) return;

          const countryName = String(
            country.country_name || country.name || country || "",
          ).trim();
          // Check if country name ends with any of the supported prefixes
          const matchedPrefix = VENDOR_SEARCH_COUNTRY_PREFIXES.find((prefix) =>
            countryName.endsWith(prefix),
          );

          if (matchedPrefix) {
            // Extract base country name by removing the prefix
            const baseCountry = countryName
              .substring(0, countryName.length - matchedPrefix.length)
              .trim();
            supported.add(baseCountry);
          }
        });

        if (isMounted) {
          setSupportedVendorCountries(supported);
        }
      } catch (error) {
        if (isMounted) {
          setSupportedVendorCountries(new Set());
        }
      }
    };

    fetchAndFilterSupportedCountries();

    return () => {
      isMounted = false;
    };
  }, []);

  const isVendorCountrySupported =
    !!userCountry && supportedVendorCountries.has(userCountry);

  const visibleTabs = useMemo(
    () => TABS.filter((tab) => tab !== "VENDOR" || isVendorCountrySupported),
    [isVendorCountrySupported],
  );

  useEffect(() => {
    if (!isVendorCountrySupported && activeTab === "VENDOR") {
      setActiveTab("EPH");
      setPage(1);
    }
  }, [isVendorCountrySupported, activeTab]);

  const mergeRowsFromResponse = useCallback((rows = []) => {
    const mappedRows = rows.map((row, index) => mapBulkConsentRow(row, index));
    setApiRows((prevRows) => {
      const byId = new Map(prevRows.map((item) => [item.id, item]));
      mappedRows.forEach((item) => {
        byId.set(item.id, item);
      });
      return Array.from(byId.values()).sort(
        (a, b) => Number(a.csvRow) - Number(b.csvRow),
      );
    });
  }, []);

  const fetchConsentResults = useCallback(
    async (orderKey = 1) => {
      const requestLastUpdatedDate = new Date().toISOString();
      const response = await api.bulkConsentResult({
        uploadId,
        order: orderKey,
        last_updated_date: requestLastUpdatedDate,
      });

      const responseData = response?.data || {};
      const rows = getResponseRows(responseData);
      mergeRowsFromResponse(rows);

      rows.forEach((row, index) => {
        const rowId = firstValue(row?.pk, row?.id, `consent-${index + 1}`);
        const status = String(
          firstValue(row?.record_process_status, row?.recordProcessStatus, ""),
        )
          .trim()
          .toUpperCase();

        if (rowId) {
          cycleStatusByIdRef.current.set(String(rowId), status);
        }
      });

      const metaData =
        responseData?.meta_data && typeof responseData.meta_data === "object"
          ? responseData.meta_data
          : responseData?.metaData && typeof responseData.metaData === "object"
            ? responseData.metaData
            : {};

      setResultMetaData({
        uploadId: firstValue(
          metaData?.upload_id,
          metaData?.uploadId,
          uploadId,
          "",
        ),
        recordCreatedDate: firstValue(
          metaData?.record_created_date,
          metaData?.recordCreatedDate,
          "",
        ),
        recordUpdatedDate: firstValue(
          metaData?.record_updated_date,
          metaData?.recordUpdatedDate,
          "",
        ),
        lastUpdatedDate: firstValue(
          metaData?.last_updated_date,
          metaData?.lastUpdatedDate,
          requestLastUpdatedDate,
        ),
      });

      const nextOrder = firstValue(
        responseData?.next_order,
        responseData?.nextOrder,
        response?.next_order,
        response?.nextOrder,
        orderKey,
      );

      const continuePolling = isTruthy(
        firstValue(
          responseData?.continue_polling,
          responseData?.continuePolling,
          response?.continue_polling,
          response?.continuePolling,
          false,
        ),
      );

      const allComplete =
        rows.length > 0 &&
        rows.every((row) => {
          const status = String(
            firstValue(
              row?.record_process_status,
              row?.recordProcessStatus,
              "",
            ),
          )
            .trim()
            .toUpperCase();
          return status === "COMPLETED";
        });

      const cycleAllComplete =
        !continuePolling &&
        cycleStatusByIdRef.current.size > 0 &&
        Array.from(cycleStatusByIdRef.current.values()).every(
          (status) => status === "COMPLETED",
        );

      return {
        nextOrder,
        continuePolling,
        allComplete,
        cycleAllComplete,
      };
    },
    [uploadId, mergeRowsFromResponse],
  );

  useEffect(() => {
    let isMounted = true;

    const clearPollTimeout = () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }

      if (autoPollingEndTimeoutRef.current) {
        clearTimeout(autoPollingEndTimeoutRef.current);
        autoPollingEndTimeoutRef.current = null;
      }

      if (manualRefreshTimeoutRef.current) {
        clearTimeout(manualRefreshTimeoutRef.current);
        manualRefreshTimeoutRef.current = null;
      }

      if (manualRefreshEndTimeoutRef.current) {
        clearTimeout(manualRefreshEndTimeoutRef.current);
        manualRefreshEndTimeoutRef.current = null;
      }
    };

    const runAutoPolling = async () => {
      if (autoPollingExpiredRef.current || allDataCompleteRef.current) {
        return;
      }

      if (currentOrderRef.current === 1) {
        cycleStatusByIdRef.current = new Map();
      }

      try {
        const pollResult = await fetchConsentResults(currentOrderRef.current);
        if (!isMounted) {
          return;
        }

        if (autoPollingExpiredRef.current || allDataCompleteRef.current) {
          return;
        }

        currentOrderRef.current = pollResult?.continuePolling
          ? pollResult?.nextOrder
          : 1;
        setApiError("");
        setIsLoading(false);

        if (pollResult?.allComplete || pollResult?.cycleAllComplete) {
          allDataCompleteRef.current = true;
          setIsAllDataComplete(true);
          setIsAutoPollingActive(false);
          setHasPollingWindowCompleted(false);
          clearPollTimeout();
          return;
        }

        pollingTimeoutRef.current = setTimeout(
          runAutoPolling,
          BULK_UPLOAD_RESULT_POLLING_INTERVAL,
        );
      } catch (error) {
        if (!isMounted) {
          return;
        }

        if (autoPollingExpiredRef.current || allDataCompleteRef.current) {
          return;
        }

        setApiError("Failed to fetch bulk consent results.");
        setIsLoading(false);

        pollingTimeoutRef.current = setTimeout(
          runAutoPolling,
          BULK_UPLOAD_RESULT_POLLING_INTERVAL,
        );
      }
    };

    const runInitialDrainPolling = async () => {
      try {
        let nextOrder = 1;
        let shouldContinue = true;
        cycleStatusByIdRef.current = new Map();

        while (isMounted && shouldContinue) {
          const pollResult = await fetchConsentResults(nextOrder);
          if (!isMounted) {
            return;
          }

          setApiError("");
          setIsLoading(false);
          nextOrder = pollResult?.nextOrder;
          shouldContinue = !!pollResult?.continuePolling;
          currentOrderRef.current = nextOrder;

          if (
            !shouldContinue &&
            (pollResult?.allComplete || pollResult?.cycleAllComplete)
          ) {
            allDataCompleteRef.current = true;
            setIsAllDataComplete(true);
            setHasPollingWindowCompleted(false);
            clearPollTimeout();
            return;
          }
        }

        if (!isMounted) {
          return;
        }

        // Once backend indicates no more immediate pages, start timed auto refresh from order 1.
        currentOrderRef.current = 1;
        pollingWindowStartRef.current = Date.now();
        autoPollingExpiredRef.current = false;
        autoPollingEndTimeoutRef.current = setTimeout(() => {
          if (!isMounted) {
            return;
          }

          autoPollingExpiredRef.current = true;
          setIsAutoPollingActive(false);
          setHasPollingWindowCompleted(true);
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
        }, BULK_UPLOAD_RESULT_TOTAL_TIME);
        setIsAutoPollingActive(true);
        runAutoPolling();
      } catch (error) {
        if (!isMounted) {
          return;
        }
        setApiError("Failed to fetch bulk consent results.");
        setIsLoading(false);
        setIsAutoPollingActive(false);
      }
    };

    if (!uploadId) {
      history.push("/upload-history");
      return undefined;
    }

    setIsLoading(true);
    setApiError("");
    setApiRows([]);
    setResultMetaData({
      uploadId: "",
      recordCreatedDate: "",
      recordUpdatedDate: "",
      lastUpdatedDate: "",
    });
    setHasPollingWindowCompleted(false);
    setIsManualRefreshLoading(false);
    setIsAutoPollingActive(false);
    setIsAllDataComplete(false);
    autoPollingExpiredRef.current = false;
    allDataCompleteRef.current = false;
    currentOrderRef.current = 1;
    runInitialDrainPolling();

    return () => {
      isMounted = false;
      clearPollTimeout();
    };
  }, [uploadId, history, fetchConsentResults]);

  const mergedRows = useMemo(() => {
    if (apiRows.length > 0) {
      return apiRows;
    }

    const legacyMapped = [
      ...ephRecords.map((row, index) => mapLegacyRow(row, "EPH", index)),
      ...vendorRecords.map((row, index) => mapLegacyRow(row, "VENDOR", index)),
      ...notFoundRecords.map((row, index) =>
        mapLegacyRow(row, "NOT FOUND", index),
      ),
    ];

    return legacyMapped;
  }, [apiRows, ephRecords, vendorRecords, notFoundRecords]);

  const tabCount = (tab) => mergedRows.filter((r) => r.category === tab).length;

  const isRefreshDisabledByAutoRefreshThreshold = useMemo(() => {
    const createdAtMs = parseDateMillis(resultMetaData.recordCreatedDate);

    if (!createdAtMs) {
      return false;
    }

    const intervalHours = Math.abs(Date.now() - createdAtMs) / (1000 * 60 * 60);
    return intervalHours > BULK_UPLOAD_RESULT_AUTO_REFRESH_INTERVAL;
  }, [resultMetaData.recordCreatedDate]);

  const shouldShowRefreshButton =
    hasPollingWindowCompleted && !isAllDataComplete;

  const displayedLastUpdatedDate = formatDateTimeValue(
    resultMetaData.lastUpdatedDate,
  );

  const handleRefreshResults = async () => {
    if (isManualRefreshLoading || allDataCompleteRef.current) {
      return;
    }

    setIsManualRefreshLoading(true);
    manualRefreshExpiredRef.current = false;

    manualRefreshEndTimeoutRef.current = setTimeout(() => {
      manualRefreshExpiredRef.current = true;
      setIsManualRefreshLoading(false);
      if (manualRefreshTimeoutRef.current) {
        clearTimeout(manualRefreshTimeoutRef.current);
        manualRefreshTimeoutRef.current = null;
      }
      if (manualRefreshEndTimeoutRef.current) {
        clearTimeout(manualRefreshEndTimeoutRef.current);
        manualRefreshEndTimeoutRef.current = null;
      }
    }, 60000);

    const runManualRefreshPolling = async (orderKey) => {
      if (manualRefreshExpiredRef.current) {
        return;
      }

      if (orderKey === 1) {
        cycleStatusByIdRef.current = new Map();
      }

      if (manualRefreshTimeoutRef.current) {
        clearTimeout(manualRefreshTimeoutRef.current);
        manualRefreshTimeoutRef.current = null;
      }

      try {
        const refreshResult = await fetchConsentResults(orderKey);
        currentOrderRef.current = refreshResult?.continuePolling
          ? refreshResult?.nextOrder
          : 1;
        setApiError("");

        if (refreshResult?.allComplete || refreshResult?.cycleAllComplete) {
          allDataCompleteRef.current = true;
          setIsAllDataComplete(true);
          setHasPollingWindowCompleted(false);
          setIsManualRefreshLoading(false);
          manualRefreshExpiredRef.current = true;
          if (manualRefreshTimeoutRef.current) {
            clearTimeout(manualRefreshTimeoutRef.current);
            manualRefreshTimeoutRef.current = null;
          }
          if (manualRefreshEndTimeoutRef.current) {
            clearTimeout(manualRefreshEndTimeoutRef.current);
            manualRefreshEndTimeoutRef.current = null;
          }
          return;
        }

        if (!refreshResult?.continuePolling) {
          setIsManualRefreshLoading(false);
          manualRefreshExpiredRef.current = true;
          if (manualRefreshTimeoutRef.current) {
            clearTimeout(manualRefreshTimeoutRef.current);
            manualRefreshTimeoutRef.current = null;
          }
          if (manualRefreshEndTimeoutRef.current) {
            clearTimeout(manualRefreshEndTimeoutRef.current);
            manualRefreshEndTimeoutRef.current = null;
          }
          return;
        }

        if (manualRefreshExpiredRef.current) {
          return;
        }

        manualRefreshTimeoutRef.current = setTimeout(() => {
          runManualRefreshPolling(refreshResult?.nextOrder);
        }, 10000);
      } catch (error) {
        setApiError("Failed to fetch bulk consent results.");
        setIsManualRefreshLoading(false);
        manualRefreshExpiredRef.current = true;
        if (manualRefreshTimeoutRef.current) {
          clearTimeout(manualRefreshTimeoutRef.current);
          manualRefreshTimeoutRef.current = null;
        }
        if (manualRefreshEndTimeoutRef.current) {
          clearTimeout(manualRefreshEndTimeoutRef.current);
          manualRefreshEndTimeoutRef.current = null;
        }
      }
    };

    currentOrderRef.current = 1;
    runManualRefreshPolling(1);
  };

  const filtered = useMemo(
    () =>
      mergedRows.filter((r) => {
        const matchTab = r.category === activeTab;
        const q = search.toLowerCase().trim();
        const rowSearchText = buildResultsRowSearchText(r);
        const queryTokens = q
          .split(/[\s,;:|/\\()[\]{}]+/)
          .filter(Boolean);
        const searchableTokens = buildResultsRowSearchTokens(r);

        const matchSearch =
          !q ||
          String(r.csvRow).toLowerCase().includes(q) ||
          rowSearchText.includes(q) ||
          queryTokens.every((token) =>
            searchableTokens.some((searchableToken) =>
              searchableToken.startsWith(token),
            ),
          );
        return matchTab && matchSearch;
      }),
    [activeTab, search, mergedRows],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginated = filtered.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize,
  );

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
  };

  const handleSearch = (q) => {
    setSearch(q);
    setPage(1);
  };

  const handlePageSize = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const joinFieldValues = (fields = []) =>
    fields.map((field) => `${field.label}: ${field.value}`).join(" | ");

  const handleExportResults = async () => {
    const extractNameFromFields = (fields = []) => {
      if (!Array.isArray(fields) || fields.length === 0) {
        return "";
      }

      const nameField = fields.find((f) => f.label === "Name");
      if (nameField && nameField.value) {
        return String(nameField.value);
      }

      const firstNameField = fields.find((f) => f.label === "First Name");
      const lastNameField = fields.find((f) => f.label === "Last Name");
      const firstName = firstNameField?.value || "";
      const lastName = lastNameField?.value || "";

      return [firstName, lastName].filter(Boolean).join(" ");
    };

    const getDcrStatus = (row) => {
      if (!row?.isDcrApplicable) {
        return "N/A";
      }

      const dcrResult = row?.dcrResult || {};

      if (dcrResult.isMissing) {
        return "N/A";
      }

      if (dcrResult.hasError) {
        return "Failed";
      }

      return "Success";
    };

    const getPhoneConsentStatus = (row) => {
      if (!row?.isConsentApplicable) {
        return "Not Applicable";
      }

      const consentResult = row?.consentResult || {};

      if (consentResult.isMissing || consentResult.phoneConsentStatus == null) {
        return "Pending";
      }

      return consentResult.phoneConsentStatus ? "Success" : "Failed";
    };

    const getEmailConsentStatus = (row) => {
      if (!row?.isConsentApplicable) {
        return "Not Applicable";
      }

      const consentResult = row?.consentResult || {};

      if (consentResult.isMissing || consentResult.emailConsentStatus == null) {
        return "Pending";
      }

      return consentResult.emailConsentStatus ? "Success" : "Failed";
    };

    const headers = [
      "Row",
      "Input",
      "HCP Name",
      "HCO Name",
      "Selected Record (HCP+HCO)",
      "Selected HCP Enterprise ID",
      "Selected HCO Enterprise ID",
      "DCR Status",
      "DCR Details",
      "Subscription Result",
      "Phone Consent Status",
      "Email Consent Status",
      "Consent Result",
    ];

    const toMultiline = (value) => String(value || "").replace(/\s\|\s/g, "\n");
    // normalize vendor display: EPH → Lilly Managed
    const toExportVendor = (v) =>
      String(v || "").toUpperCase() === "EPH" ? "Lilly Managed" : v;

    const getSelectedIds = (row) => {
      const primaryLabel = String(row?.selectionPrimary?.label || "")
        .trim()
        .toUpperCase();
      const parentLabel = String(row?.selectionParent?.label || "")
        .trim()
        .toUpperCase();

      const selectedHcpEnterpriseId =
        primaryLabel === "HCP" ? String(row?.selectionPrimary?.id || "") : "";

      const selectedHcoEnterpriseId = primaryLabel.includes("HCO")
        ? String(row?.selectionPrimary?.id || "")
        : parentLabel.includes("HCO")
          ? String(row?.selectionParent?.id || "")
          : "";

      return {
        selectedHcpEnterpriseId,
        selectedHcoEnterpriseId,
      };
    };

    const getDcrResultText = (row) => {
      if (!row?.isDcrApplicable) {
        return "Not Applicable";
      }

      const dcrResult = row?.dcrResult || {};
      const lines = [];
      const addLine = (key, value) => {
        if (value === undefined || value === null || value === "") {
          return;
        }
        lines.push(`${key}: ${String(value)}`);
      };

      if (dcrResult.isMissing) {
        return "No DCR details available";
      }

      if (dcrResult.hasError) {
        lines.push("Status: Failed");
        addLine("Processed", dcrResult.processedStatus);
        addLine("Validation", dcrResult.validationStatus);
        addLine("Error Type", dcrResult.errorType);
        addLine("Error Message", dcrResult.errorMessage);
      } else {
        const dcrIdItems = dcrResult.dcrIdItems || [];
        const resultItems = dcrResult.resultItems || [];

        dcrIdItems.forEach((item, index) => {
          addLine(`DCR Id ${index + 1}`, item?.id);
          addLine(`DCR Id ${index + 1} Vendor`, toExportVendor(item?.vendor));
          if (item?.status !== null && item?.status !== undefined) {
            addLine(
              `DCR Id ${index + 1} Status`,
              item?.status ? "Success" : "Failed",
            );
          }
        });

        resultItems.forEach((item, index) => {
          addLine(`DCR Id ${dcrIdItems.length + index + 1}`, item?.id);
          addLine(
            `DCR Id ${dcrIdItems.length + index + 1} Vendor`,
            toExportVendor(item?.vendor),
          );
        });

        const fallbackItems = (dcrResult.dcrItems || []).filter(
          (item) => item?.sourceType === "fallback",
        );
        fallbackItems.forEach((item, index) => {
          addLine(
            `DCR Id ${dcrIdItems.length + resultItems.length + index + 1}`,
            item?.id,
          );
          addLine(
            `DCR Id ${dcrIdItems.length + resultItems.length + index + 1} Vendor`,
            toExportVendor(item?.vendor),
          );
        });

        if (dcrResult.dcrOutcomeResult) {
          const outcome = dcrResult.dcrOutcomeResult;
          addLine("HCP Enterprise ID", outcome?.hcpEnterpriseId);
          addLine("HCO Enterprise ID", outcome?.hcoEnterpriseId);
          addLine("HCP Entity ID", outcome?.hcpEntityId);
          addLine("HCO Entity ID", outcome?.hcoEntityId);
        }

        if (lines.length === 0) {
          return "No DCR details available";
        }
      }

      return lines.join("\n");
    };

    const getSubscriptionResultText = (row) => {
      if (!row?.isSubscriptionApplicable) {
        return "Not Applicable";
      }

      const subscription = row?.subscriptionResult || {};
      const lines = [];
      const addLine = (key, value) => {
        if (value === undefined || value === null || value === "") {
          return;
        }
        lines.push(`${key}: ${String(value)}`);
      };

      const errors = Array.isArray(subscription.errors)
        ? subscription.errors
        : [];
      const shellFailed = subscription.shellProfileStatus === false;

      if (shellFailed || errors.length > 0) {
        lines.push("Status: Failed");
        if (errors.length > 0) {
          errors.forEach((error, index) =>
            addLine(`Error ${index + 1}`, error),
          );
        } else {
          addLine("Error", "Shell profile failed.");
        }
        return lines.join("\n");
      }

      const entityDetails = Array.isArray(subscription.entityDetails)
        ? subscription.entityDetails
        : [];

      entityDetails.forEach((entity, index) => {
        const prefix = entity?.label
          ? String(entity.label)
          : `Entity ${index + 1}`;
        addLine(`${prefix} Source`, mapSourceLabel(entity?.source));
        addLine(`${prefix} Enterprise ID`, entity?.enterpriseId);
        addLine(`${prefix} Entity ID`, entity?.entityId);
        if (entity?.foundInEph !== undefined && entity?.foundInEph !== null) {
          addLine(
            `${prefix} OneKey ID Found In EPH`,
            isTruthy(entity?.foundInEph) ? "Yes" : "No",
          );
        }
      });

      if (lines.length === 0) {
        return "Pending";
      }

      if (subscription.shellProfileStatus === true) {
        lines.unshift("Status: Success");
      }

      return lines.join("\n");
    };

    const getConsentResultText = (row) => {
      if (!row?.isConsentApplicable) {
        return "Not Applicable";
      }

      const consentResult = row?.consentResult || {};
      if (consentResult.isMissing) {
        return "Pending";
      }

      if (
        consentResult.consentUploaded === false ||
        consentResult.emailConsentStatus === false ||
        consentResult.phoneConsentStatus === false ||
        consentResult.emailConsentError ||
        consentResult.phoneConsentError
      ) {
        return "Failed";
      }

      if (
        consentResult.consentUploaded === true ||
        consentResult.emailConsentStatus === true ||
        consentResult.phoneConsentStatus === true
      ) {
        return "Success";
      }

      return "Pending";
    };

    const buildCompleteSelectionData = (selectionObj) => {
      if (!selectionObj) {
        return "";
      }

      const parts = [
        `Label: ${selectionObj.label}`,
        `ID Type: ${selectionObj.idType}`,
        `Source: ${selectionObj.source}`,
        `ID: ${selectionObj.id}`,
      ];

      if (selectionObj.fields && selectionObj.fields.length > 0) {
        selectionObj.fields.forEach((f) => {
          parts.push(`${f.label}: ${f.value}`);

          if (f.details && f.details.length > 0) {
            f.details.forEach((detail) => {
              parts.push(`  ${detail.label}: ${detail.value}`);
            });
          }
        });
      }

      return parts.join("\n");
    };

    const createSheetForTab = (tabName) => {
      const headerRow = headers.map((header) => ({
        value: header,
        fontWeight: "bold",
        textColor: "#FFFFFF",
        fontSize: 11,
        backgroundColor: "#CC1F1F",
        alignVertical: "center",
        align: "left",
        wrap: true,
        height: 24,
        borderColor: "#E5E7EB",
        borderStyle: "thin",
      }));

      const tabRows = mergedRows.filter((r) => r.category === tabName);
      const dataRows = tabRows.map((r, rowIndex) => {
        const inputFields = [
          ...r.inputHcpFields,
          ...r.inputHcoFields,
          ...r.inputParentHcoFields,
        ].filter((field) => field.label !== "Name");
        const inputSummary = [joinFieldValues(inputFields)]
          .filter(Boolean)
          .join(" | ");
        const selectedSummary = [
          buildCompleteSelectionData(r.selectionPrimary),
          buildCompleteSelectionData(r.selectionParent),
        ]
          .filter(Boolean)
          .join("\n\n");

        const { selectedHcpEnterpriseId, selectedHcoEnterpriseId } =
          getSelectedIds(r);
        const dcrResultText = getDcrResultText(r);
        const dcrStatus = getDcrStatus(r);
        const subscriptionResultText = getSubscriptionResultText(r);
        const phoneConsentStatus = getPhoneConsentStatus(r);
        const emailConsentStatus = getEmailConsentStatus(r);
        const consentResultText = getConsentResultText(r);

        const hcpName = extractNameFromFields(r.inputHcpFields);
        const hcoName =
          r.inputParentHcoFields.find(
            (field) => field.label.toLowerCase() === "rel hco name",
          )?.value || "";

        const rowValues = [
          r.csvRow,
          toMultiline(inputSummary),
          hcpName || "N/A",
          hcoName || "N/A",
          toMultiline(selectedSummary),
          selectedHcpEnterpriseId || "N/A",
          selectedHcoEnterpriseId || "N/A",
          dcrStatus,
          dcrResultText,
          subscriptionResultText,
          phoneConsentStatus,
          emailConsentStatus,
          consentResultText,
        ];

        const isEvenRow = (rowIndex + 1) % 2 === 0;

        return rowValues.map((value) => ({
          value: value ?? "",
          alignVertical: "top",
          align: "left",
          wrap: true,
          height: 64,
          borderColor: "#F3F4F6",
          borderStyle: "thin",
          ...(isEvenRow ? { backgroundColor: "#F9FAFB" } : {}),
        }));
      });

      const colWidths = [
        10, 42, 25, 25, 42, 24, 24, 15, 40, 44, 20, 20, 18,
      ].map((width) => ({ width }));

      return {
        sheet: tabName,
        columns: colWidths,
        stickyRowsCount: 1,
        data: [headerRow, ...dataRows],
      };
    };

    const sheets = TABS.filter(
      (tab) => tab !== "VENDOR" || isVendorCountrySupported,
    ).map((tab) => createSheetForTab(tab));

    const blob = await writeExcelFile(sheets).toBlob();
    FileSaver.saveAs(blob, `${uploadId}_export.xlsx`);
  };

  return (
    <div
      className="container body-height extra-margin-top-less mb-4"
      style={{ maxWidth: 1300 }}
    >
      <Card sx={{ maxWidth: 1300 }} className="mx-auto bulk-results-shell">
        <Box className="mx-5 my-4">
          <div>
            <div style={{ margin: "0 auto", maxWidth: 1200 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 20,
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <h1
                    style={{
                      margin: 0,
                      fontSize: 22,
                      fontWeight: 700,
                      color: "#111827",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    Results
                  </h1>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Chip
                    label={`Upload ID: ${uploadId}`}
                    sx={{
                      backgroundColor: "#f0f9ff",
                      borderColor: "#0ea5e9",
                      color: "#0c4a6e",
                      fontWeight: 600,
                      border: "2px solid #0ea5e9",
                      fontSize: "0.90rem",
                      height: "auto",
                      padding: "4px 8px",
                      "& .MuiChip-label": {
                        paddingLeft: "6px",
                        paddingRight: "6px",
                      },
                    }}
                    variant="outlined"
                  />
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    width: "86%",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  {visibleTabs.map((tab) => {
                    const isActive = activeTab === tab;
                    return (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => handleTabChange(tab)}
                        style={{
                          position: "relative",
                          padding: "6px 16px",
                          fontSize: 14,
                          fontWeight: 600,
                          borderRadius: 8,
                          border: "none",
                          whiteSpace: "nowrap",
                          cursor: "pointer",
                          color: isActive ? "#fff" : "#6b7280",
                          background: isActive ? "#cc1f1f" : "transparent",
                        }}
                      >
                        {tab} ({tabCount(tab)})
                        {isActive && (
                          <span
                            style={{
                              position: "absolute",
                              left: "50%",
                              transform: "translateX(-50%)",
                              bottom: -4,
                              width: "80%",
                              height: 3,
                              borderRadius: 999,
                              background: "#7c3aed",
                            }}
                          />
                        )}
                      </button>
                    );
                  })}

                  {shouldShowRefreshButton && (
                    <>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#4b5563",
                          whiteSpace: "nowrap",
                          marginLeft: 6,
                        }}
                      >
                        Last Updated(UTC): {displayedLastUpdatedDate}
                      </span>
                      <button
                        type="button"
                        onClick={handleRefreshResults}
                        disabled={
                          isManualRefreshLoading ||
                          isRefreshDisabledByAutoRefreshThreshold
                        }
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          background: "#ffffff",
                          color: "#374151",
                          border: "1px solid #d1d5db",
                          fontSize: 13,
                          fontWeight: 600,
                          padding: "8px 12px",
                          borderRadius: 10,
                          cursor:
                            isManualRefreshLoading ||
                            isRefreshDisabledByAutoRefreshThreshold
                              ? "not-allowed"
                              : "pointer",
                          opacity:
                            isManualRefreshLoading ||
                            isRefreshDisabledByAutoRefreshThreshold
                              ? 0.65
                              : 1,
                        }}
                      >
                        {isManualRefreshLoading ? (
                          <CircularProgress size={14} />
                        ) : (
                          <RefreshIcon sx={{ fontSize: 15 }} />
                        )}
                        Refresh
                      </button>
                    </>
                  )}

                  {isAutoPollingActive &&
                    !hasPollingWindowCompleted &&
                    !isAllDataComplete && (
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "8px 12px",
                          borderRadius: 10,
                          border: "1px solid #fde68a",
                          background: "#fffbeb",
                          color: "#92400e",
                          fontSize: 13,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <CircularProgress size={14} sx={{ color: "#d97706" }} />
                        <span>Fetching results...</span>
                      </div>
                    )}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 8,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <button
                      type="button"
                      onClick={() => setShowHelpModal(true)}
                      title="View help documentation for this page"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        background: "#fff",
                        color: "#374151",
                        border: "1px solid #d1d5db",
                        fontSize: 13,
                        fontWeight: 500,
                        padding: "6px 12px",
                        borderRadius: 8,
                        cursor: "pointer",
                      }}
                    >
                      <HelpOutlineIcon sx={{ fontSize: 16 }} />
                      Help
                    </button>
                    <button
                      type="button"
                      onClick={handleExportResults}
                      disabled={isLoading || mergedRows.length === 0}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        background: "#cc1f1f",
                        color: "#fff",
                        border: "none",
                        fontSize: 14,
                        fontWeight: 600,
                        padding: "10px 20px",
                        borderRadius: 12,
                        width: 155,
                        justifyContent: "center",
                        cursor:
                          isLoading || mergedRows.length === 0
                            ? "not-allowed"
                            : "pointer",
                        opacity: isLoading || mergedRows.length === 0 ? 0.6 : 1,
                        boxShadow: "0 4px 12px rgba(204,31,31,0.25)",
                      }}
                    >
                      <DownloadIcon sx={{ fontSize: 16 }} />
                      Export Results
                    </button>
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginBottom: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ position: "relative", width: 288 }}>
                  <SearchIcon
                    sx={{
                      fontSize: 16,
                      color: "#9ca3af",
                      position: "absolute",
                      left: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Search records..."
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px 12px 8px 36px",
                      fontSize: 14,
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      background: "#fff",
                      outline: "none",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    }}
                  />
                </div>

                <div
                  style={{
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: "10px 12px",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    overflowX: "auto",
                  }}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 14,
                      flexWrap: "nowrap",
                      whiteSpace: "nowrap",
                      color: "#374151",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <span className="right-tick-cs-result">✓</span>
                      <span>Applicable / Success</span>
                    </span>
                    <span style={{ color: "#d1d5db" }}>|</span>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <AccessTimeIcon sx={{ fontSize: 18, color: "#d97706" }} />
                      <span>Pending</span>
                    </span>
                    <span style={{ color: "#d1d5db" }}>|</span>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <DoNotDisturbAltIcon
                        sx={{ fontSize: 18, color: "#dc2626" }}
                      />
                      <span>Not Applicable</span>
                    </span>
                    <span style={{ color: "#d1d5db" }}>|</span>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <ErrorIcon sx={{ fontSize: 18, color: "#dc2626" }} />
                      <span>Failed / Error</span>
                    </span>
                    <span style={{ color: "#d1d5db" }}>|</span>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <InfoOutlinedIcon
                        sx={{ fontSize: 18, color: "#9ca3af" }}
                      />
                      <span>View Details</span>
                    </span>
                  </div>
                </div>
              </div>

              <div
                style={{
                  background: "#fff",
                  borderRadius: 16,
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  overflow: "hidden",
                }}
              >
                <div style={{ overflowX: "hidden" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 14,
                      tableLayout: "fixed",
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          borderBottom: "1px solid #e5e7eb",
                          background: "#f9fafb",
                        }}
                      >
                        {[
                          { label: "Row", w: "6%" },
                          { label: "Input HCP/HCO", w: "23%" },
                          { label: "Input Parent HCO", w: "23%" },
                          { label: "Selection From Search Results", w: "32%" },
                          { label: "Result", w: "16%" },
                        ].map((col) => (
                          <th
                            key={col.label}
                            style={{
                              width: col.w,
                              textAlign: "left",
                              padding: "12px 16px",
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#3a6fa5",
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td
                            colSpan={6}
                            style={{
                              textAlign: "center",
                              padding: "56px 0",
                              color: "#6b7280",
                              fontSize: 14,
                            }}
                          >
                            <CircularProgress size={22} sx={{ mr: 1 }} />{" "}
                            Loading results...
                          </td>
                        </tr>
                      ) : apiError ? (
                        <tr>
                          <td
                            colSpan={6}
                            style={{
                              textAlign: "center",
                              padding: "56px 0",
                              color: "#b91c1c",
                              fontSize: 14,
                            }}
                          >
                            {apiError}
                          </td>
                        </tr>
                      ) : paginated.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            style={{
                              textAlign: "center",
                              padding: "56px 0",
                              color: "#9ca3af",
                              fontSize: 14,
                            }}
                          >
                            No records found.
                          </td>
                        </tr>
                      ) : (
                        paginated.map((r, idx) => {
                          const rowBg = idx % 2 === 0 ? "#f0faf8" : "#fff";
                          return (
                            <tr
                              key={r.csvRow}
                              style={{
                                borderBottom: "1px solid #f3f4f6",
                                background: rowBg,
                              }}
                            >
                              <td
                                style={{
                                  padding: "16px",
                                  verticalAlign: "top",
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 14,
                                    fontWeight: 700,
                                    color: idx === 0 ? "#cc1f1f" : "#6b7280",
                                  }}
                                >
                                  {r.csvRow}
                                </span>
                              </td>

                              <td
                                style={{
                                  padding: 16,
                                  verticalAlign: "top",
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                }}
                              >
                                {r.inputHcpFields.length === 0 &&
                                r.inputHcoFields.length === 0 ? (
                                  <span
                                    style={{ fontSize: 12, color: "#9ca3af" }}
                                  >
                                    -
                                  </span>
                                ) : (
                                  (() => {
                                    const compact = renderFieldLines(
                                      [
                                        ...r.inputHcpFields,
                                        ...r.inputHcoFields,
                                      ],
                                      3,
                                    );

                                    return (
                                      <HoverCell
                                        primary={compact.primary}
                                        details={compact.details}
                                      />
                                    );
                                  })()
                                )}
                              </td>

                              <td
                                style={{
                                  padding: 16,
                                  verticalAlign: "top",
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                }}
                              >
                                {r.inputParentHcoFields.length === 0 ? (
                                  <span
                                    style={{ fontSize: 12, color: "#9ca3af" }}
                                  >
                                    -
                                  </span>
                                ) : (
                                  (() => {
                                    const compact = renderFieldLines(
                                      r.inputParentHcoFields,
                                      3,
                                    );
                                    return (
                                      <HoverCell
                                        primary={compact.primary}
                                        details={compact.details}
                                      />
                                    );
                                  })()
                                )}
                              </td>

                              <td
                                style={{
                                  padding: 16,
                                  verticalAlign: "top",
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                }}
                              >
                                {(() => {
                                  const noSelectionItems =
                                    getNoSelectionItems(r);
                                  if (
                                    !r.selectionPrimary &&
                                    !r.selectionParent &&
                                    noSelectionItems.length === 0
                                  ) {
                                    return (
                                      <span
                                        style={{
                                          fontSize: 12,
                                          color: "#9ca3af",
                                        }}
                                      >
                                        -
                                      </span>
                                    );
                                  }

                                  return (() => {
                                    const primaryCompact = r.selectionPrimary
                                      ? renderFieldLines(
                                          r.selectionPrimary.fields || [],
                                          6,
                                        )
                                      : null;
                                    const parentCompact = r.selectionParent
                                      ? renderFieldLines(
                                          r.selectionParent.fields || [],
                                          6,
                                        )
                                      : null;

                                    return (
                                      <div style={{ display: "grid", gap: 8 }}>
                                        {primaryCompact && (
                                          <HoverCell
                                            label={formatSelectionHeading(
                                              r.selectionPrimary?.label ||
                                                "Selection",
                                              r.selectionPrimary?.source,
                                            )}
                                            primary={primaryCompact.primary}
                                            details={primaryCompact.details}
                                          />
                                        )}

                                        {parentCompact && (
                                          <HoverCell
                                            label={formatSelectionHeading(
                                              r.selectionParent?.label ||
                                                "Parent Selection",
                                              r.selectionParent?.source,
                                            )}
                                            primary={parentCompact.primary}
                                            details={parentCompact.details}
                                          />
                                        )}

                                        {noSelectionItems.map((item) => (
                                          <span
                                            key={`${r.id}-${item.key}`}
                                            style={{
                                              display: "inline-flex",
                                              alignItems: "center",
                                              width: "fit-content",
                                              padding: "3px 8px",
                                              borderRadius: 999,
                                              border: `1px solid ${item.borderColor}`,
                                              background: item.background,
                                              color: item.textColor,
                                              fontSize: 11,
                                              fontWeight: 700,
                                              letterSpacing: 0.2,
                                            }}
                                          >
                                            {item.label}
                                          </span>
                                        ))}
                                      </div>
                                    );
                                  })();
                                })()}
                              </td>

                              <td
                                style={{
                                  padding: 16,
                                  verticalAlign: "top",
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                }}
                              >
                                {(() => {
                                  const applicabilityItems =
                                    buildApplicabilityItems(r);
                                  const hasFailedStatus =
                                    applicabilityItems.some(
                                      (item) => item.status === "failed",
                                    );

                                  return (
                                    <>
                                      <div style={{ display: "grid", gap: 10 }}>
                                        {applicabilityItems.map((item) => (
                                          <div
                                            key={`${r.id}-${item.key}`}
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "space-between",
                                              width: "100%",
                                              fontSize: 13,
                                              fontWeight: 600,
                                              color:
                                                item.status === "success"
                                                  ? "#166534"
                                                  : item.status === "pending"
                                                    ? "#b45309"
                                                    : "#b91c1c",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 6,
                                              }}
                                            >
                                              {item.status === "success" ? (
                                                <span className="right-tick-cs-result">
                                                  ✓
                                                </span>
                                              ) : item.status === "pending" ? (
                                                <AccessTimeIcon
                                                  sx={{
                                                    fontSize: 18,
                                                    color: "#d97706",
                                                  }}
                                                />
                                              ) : item.status === "failed" ? (
                                                <ErrorIcon
                                                  sx={{
                                                    fontSize: 18,
                                                    color: "#dc2626",
                                                  }}
                                                />
                                              ) : (
                                                <DoNotDisturbAltIcon
                                                  sx={{
                                                    fontSize: 18,
                                                    color: "#dc2626",
                                                  }}
                                                />
                                              )}
                                              <span>{item.label}</span>
                                            </div>
                                            {item.key === "dcr" &&
                                              r.category !== "NO ACTION" && (
                                                <button
                                                  type="button"
                                                  onClick={(event) =>
                                                    handleOpenDetailsPopup(
                                                      event,
                                                      r,
                                                    )
                                                  }
                                                  aria-label="View all result details"
                                                  style={{
                                                    border: "none",
                                                    background: "transparent",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    cursor: "pointer",
                                                    padding: 0,
                                                    color: hasFailedStatus
                                                      ? "#dc2626"
                                                      : "#166534",
                                                  }}
                                                >
                                                  <InfoOutlinedIcon
                                                    sx={{ fontSize: 16 }}
                                                  />
                                                </button>
                                              )}
                                          </div>
                                        ))}
                                      </div>
                                    </>
                                  );
                                })()}
                                {/* <div style={{ marginTop: 6 }}>
                            <button
                              type="button"
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: "#374151",
                                border: "1px solid #d1d5db",
                                borderRadius: 6,
                                padding: "4px 10px",
                                background: "#fff",
                                cursor: "pointer",
                              }}
                            >
                              View DCR
                            </button>
                          </div> */}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <div
                  style={{
                    padding: "12px 20px",
                    borderTop: "1px solid #f3f4f6",
                    background: "rgba(249,250,251,0.6)",
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                      flexWrap: "wrap",
                    }}
                  >
                    <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>
                      Showing{" "}
                      <span style={{ fontWeight: 600, color: "#374151" }}>
                        {filtered.length === 0
                          ? 0
                          : (safePage - 1) * pageSize + 1}
                        -{Math.min(safePage * pageSize, filtered.length)}
                      </span>{" "}
                      of{" "}
                      <span style={{ fontWeight: 600, color: "#374151" }}>
                        {filtered.length}
                      </span>{" "}
                      records
                    </p>

                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <span style={{ fontSize: 12, color: "#6b7280" }}>
                        Rows per page:
                      </span>
                      <select
                        value={pageSize}
                        onChange={(e) => handlePageSize(Number(e.target.value))}
                        style={{
                          fontSize: 12,
                          color: "#374151",
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          padding: "6px 8px",
                          background: "#fff",
                          cursor: "pointer",
                        }}
                      >
                        {[10, 20, 30, 40, 50].map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <button
                      type="button"
                      onClick={() => setPage(1)}
                      disabled={safePage === 1}
                      style={{
                        border: "none",
                        background: "transparent",
                        cursor: safePage === 1 ? "not-allowed" : "pointer",
                        opacity: safePage === 1 ? 0.3 : 1,
                        padding: 6,
                      }}
                      title="First page"
                    >
                      <KeyboardDoubleArrowLeftIcon
                        sx={{ fontSize: 16, color: "#4b5563" }}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={safePage === 1}
                      style={{
                        border: "none",
                        background: "transparent",
                        cursor: safePage === 1 ? "not-allowed" : "pointer",
                        opacity: safePage === 1 ? 0.3 : 1,
                        padding: 6,
                      }}
                      title="Previous page"
                    >
                      <KeyboardArrowLeftIcon
                        sx={{ fontSize: 16, color: "#4b5563" }}
                      />
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(
                        (p) =>
                          p === 1 ||
                          p === totalPages ||
                          Math.abs(p - safePage) <= 1,
                      )
                      .reduce((acc, p, i, arr) => {
                        if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, i) =>
                        p === "..." ? (
                          <span
                            key={`ellipsis-${i}`}
                            style={{
                              padding: "0 4px",
                              fontSize: 12,
                              color: "#9ca3af",
                            }}
                          >
                            ...
                          </span>
                        ) : (
                          <button
                            key={p}
                            type="button"
                            onClick={() => setPage(p)}
                            style={{
                              minWidth: 30,
                              height: 30,
                              borderRadius: 6,
                              border: "none",
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: "pointer",
                              background:
                                safePage === p ? "#cc1f1f" : "transparent",
                              color: safePage === p ? "#fff" : "#4b5563",
                              boxShadow:
                                safePage === p
                                  ? "0 1px 2px rgba(0,0,0,0.15)"
                                  : "none",
                            }}
                          >
                            {p}
                          </button>
                        ),
                      )}

                    <button
                      type="button"
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={safePage === totalPages}
                      style={{
                        border: "none",
                        background: "transparent",
                        cursor:
                          safePage === totalPages ? "not-allowed" : "pointer",
                        opacity: safePage === totalPages ? 0.3 : 1,
                        padding: 6,
                      }}
                      title="Next page"
                    >
                      <KeyboardArrowRightIcon
                        sx={{ fontSize: 16, color: "#4b5563" }}
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPage(totalPages)}
                      disabled={safePage === totalPages}
                      style={{
                        border: "none",
                        background: "transparent",
                        cursor:
                          safePage === totalPages ? "not-allowed" : "pointer",
                        opacity: safePage === totalPages ? 0.3 : 1,
                        padding: 6,
                      }}
                      title="Last page"
                    >
                      <KeyboardDoubleArrowRightIcon
                        sx={{ fontSize: 16, color: "#4b5563" }}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <Popover
                open={isDetailsPopupOpen}
                anchorEl={detailsPopupState.anchorEl}
                onClose={handleCloseDetailsPopup}
                disableScrollLock
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                PaperProps={{
                  sx: {
                    p: 1.5,
                    width: "auto",
                    minWidth: 340,
                    maxWidth: "calc(100vw - 24px)",
                    borderRadius: 2,
                    border: "1px solid #e5e7eb",
                    overflowX: "auto",
                  },
                }}
              >
                {detailsPopupRow ? (
                  <div
                    style={{
                      display: "grid",
                      gap: 10,
                      fontSize: 12,
                      color: "#374151",
                      overflowX: "auto",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#111827",
                      }}
                    >
                      Result Details
                    </div>

                    {showDcrDetailsSection && (
                      <div
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: 8,
                          padding: "8px 10px",
                          display: "grid",
                          gap: 6,
                        }}
                      >
                        {(() => {
                          if (isDcrPopupPending) {
                            return (
                              <>
                                <div
                                  style={{ fontWeight: 700, color: "#b45309" }}
                                >
                                  DCR Details
                                </div>
                                <div>
                                  <span style={{ color: "#6b7280" }}>
                                    Status:{" "}
                                  </span>
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      color: "#b45309",
                                    }}
                                  >
                                    Pending
                                  </span>
                                </div>
                              </>
                            );
                          }

                          const popupDcrResult = detailsPopupDcrResult;
                          const dcrIdItems = popupDcrResult?.dcrIdItems || [];
                          const resultItems = popupDcrResult?.resultItems || [];
                          const fallbackItems = (
                            popupDcrResult?.dcrItems || []
                          ).filter(
                            (dcrItem) => dcrItem?.sourceType === "fallback",
                          );
                          const dcrDetailsItems = [
                            ...dcrIdItems,
                            ...resultItems,
                            ...fallbackItems,
                          ];
                          const dcrOutcome =
                            popupDcrResult?.dcrOutcomeResult || null;

                          const hcpEnterpriseId =
                            dcrOutcome?.hcpEnterpriseId || "";
                          const hcpEntityId = dcrOutcome?.hcpEntityId || "";
                          const hcoEnterpriseId =
                            dcrOutcome?.hcoEnterpriseId || "";
                          const hcoEntityId = dcrOutcome?.hcoEntityId || "";
                          const hasDcrResults =
                            !!hcpEnterpriseId || !!hcoEnterpriseId;
                          const hcpEphLink = createEPHLink(hcpEntityId);
                          const hcoEphLink = createEPHLink(hcoEntityId);

                          return (
                            <>
                              <div
                                style={{
                                  fontWeight: 700,
                                  color: popupDcrResult?.hasError
                                    ? "#b91c1c"
                                    : "#166534",
                                }}
                              >
                                DCR Details
                              </div>

                              {popupDcrResult?.hasError ? (
                                <>
                                  <div>
                                    <span style={{ color: "#6b7280" }}>
                                      Status:{" "}
                                    </span>
                                    <span
                                      style={{
                                        fontWeight: 600,
                                        color: "#b91c1c",
                                      }}
                                    >
                                      Failed
                                    </span>
                                  </div>
                                  {popupDcrResult?.errorType && (
                                    <div>
                                      <span style={{ color: "#6b7280" }}>
                                        Error Type:{" "}
                                      </span>
                                      <span style={{ fontWeight: 600 }}>
                                        {popupDcrResult?.errorType}
                                      </span>
                                    </div>
                                  )}
                                  {popupDcrResult?.errorMessage && (
                                    <div>
                                      <span style={{ color: "#6b7280" }}>
                                        Error Message:{" "}
                                      </span>
                                      <span
                                        style={{
                                          fontWeight: 600,
                                          whiteSpace: "pre-wrap",
                                        }}
                                      >
                                        {popupDcrResult?.errorMessage}
                                      </span>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <>
                                  {dcrDetailsItems.length > 0 && (
                                    <div style={{ display: "grid", gap: 4 }}>
                                      {dcrIdItems.map((dcrItem, index) => (
                                        <div
                                          key={`${dcrItem.id}-dcr-id-${index}`}
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 4,
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          <span
                                            style={{
                                              color: "#6b7280",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {`DCR Id ${index + 1}:`}
                                          </span>
                                          {dcrItem.link ? (
                                            <a
                                              href={dcrItem.link}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                fontWeight: 600,
                                                whiteSpace: "nowrap",
                                                color: "#2563eb",
                                                textDecoration: "underline",
                                              }}
                                            >
                                              {dcrItem.id || "-"}
                                            </a>
                                          ) : (
                                            <span
                                              style={{
                                                fontWeight: 600,
                                                whiteSpace: "nowrap",
                                              }}
                                            >
                                              {dcrItem.id || "-"}
                                            </span>
                                          )}
                                          <span
                                            style={{
                                              fontWeight: 600,
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            (
                                            {String(
                                              dcrItem.vendor || "-",
                                            ).toUpperCase() === "EPH"
                                              ? "Lilly Managed"
                                              : dcrItem.vendor || "-"}
                                            )
                                          </span>
                                        </div>
                                      ))}

                                      {resultItems.map((dcrItem, index) => (
                                        <div
                                          key={`${dcrItem.id}-result-${index}`}
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 4,
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          <span
                                            style={{
                                              color: "#6b7280",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {`DCR Id ${dcrIdItems.length + index + 1}:`}
                                          </span>
                                          {dcrItem.link ? (
                                            <a
                                              href={dcrItem.link}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                fontWeight: 600,
                                                whiteSpace: "nowrap",
                                                color: "#2563eb",
                                                textDecoration: "underline",
                                              }}
                                            >
                                              {dcrItem.id || "-"}
                                            </a>
                                          ) : (
                                            <span
                                              style={{
                                                fontWeight: 600,
                                                whiteSpace: "nowrap",
                                              }}
                                            >
                                              {dcrItem.id || "-"}
                                            </span>
                                          )}
                                          <span
                                            style={{
                                              fontWeight: 600,
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            (
                                            {String(
                                              dcrItem.vendor || "-",
                                            ).toUpperCase() === "EPH"
                                              ? "Lilly Managed"
                                              : dcrItem.vendor || "-"}
                                            )
                                          </span>
                                        </div>
                                      ))}

                                      {fallbackItems.map((dcrItem, index) => (
                                        <div
                                          key={`${dcrItem.id}-fallback-${index}`}
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 4,
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          <span
                                            style={{
                                              color: "#6b7280",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {`DCR Id ${dcrIdItems.length + resultItems.length + index + 1}:`}
                                          </span>
                                          <span
                                            style={{
                                              fontWeight: 600,
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            {dcrItem.id || "-"}
                                          </span>
                                          <span
                                            style={{
                                              fontWeight: 600,
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            (
                                            {String(
                                              dcrItem.vendor || "-",
                                            ).toUpperCase() === "EPH"
                                              ? "Lilly Managed"
                                              : dcrItem.vendor || "-"}
                                            )
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {hasDcrResults && (
                                    <div
                                      style={{
                                        border: "1px solid #e5e7eb",
                                        borderRadius: 6,
                                        padding: "6px 8px",
                                        display: "grid",
                                        gap: 4,
                                      }}
                                    >
                                      {hcpEnterpriseId && (
                                        <div>
                                          <span style={{ color: "#6b7280" }}>
                                            HCP Enterprise ID:
                                          </span>
                                          {hcpEphLink ? (
                                            <a
                                              href={hcpEphLink}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                fontWeight: 600,
                                                color: "#2563eb",
                                                textDecoration: "underline",
                                              }}
                                            >
                                              {hcpEnterpriseId}
                                            </a>
                                          ) : (
                                            <span style={{ fontWeight: 600 }}>
                                              {hcpEnterpriseId}
                                            </span>
                                          )}
                                        </div>
                                      )}

                                      {hcoEnterpriseId && (
                                        <div>
                                          <span style={{ color: "#6b7280" }}>
                                            HCO Enterprise ID:
                                          </span>
                                          {hcoEphLink ? (
                                            <a
                                              href={hcoEphLink}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                fontWeight: 600,
                                                color: "#2563eb",
                                                textDecoration: "underline",
                                              }}
                                            >
                                              {hcoEnterpriseId}
                                            </a>
                                          ) : (
                                            <span style={{ fontWeight: 600 }}>
                                              {hcoEnterpriseId}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {popupDcrResult?.processedStatus && (
                                    <div>
                                      <span style={{ color: "#6b7280" }}>
                                        Processed:{" "}
                                      </span>
                                      <span style={{ fontWeight: 600 }}>
                                        {popupDcrResult?.processedStatus}
                                      </span>
                                    </div>
                                  )}
                                  {popupDcrResult?.validationStatus && (
                                    <div>
                                      <span style={{ color: "#6b7280" }}>
                                        Validation:{" "}
                                      </span>
                                      <span style={{ fontWeight: 600 }}>
                                        {popupDcrResult?.validationStatus}
                                      </span>
                                    </div>
                                  )}

                                  {!hasDcrResults &&
                                    dcrDetailsItems.length === 0 && (
                                      <div
                                        style={{
                                          color: "#6b7280",
                                          fontStyle: "italic",
                                        }}
                                      >
                                        No DCR details available
                                      </div>
                                    )}
                                </>
                              )}

                              {!popupDcrResult?.isMissing && (
                                <div>
                                  <span style={{ color: "#6b7280" }}>
                                    Updated Date:{" "}
                                  </span>
                                  <span style={{ fontWeight: 600 }}>
                                    {formatDateOnly(
                                      detailsPopupRow?.updatedDate,
                                    )}
                                  </span>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}

                    {showSubscriptionDetailsSection && (
                      <div
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: 8,
                          padding: "8px 10px",
                          display: "grid",
                          gap: 6,
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 700,
                            color: hasSubscriptionFailureDetails
                              ? "#b91c1c"
                              : "#166534",
                          }}
                        >
                          Subscription Details
                        </div>

                        {hasSubscriptionFailureDetails ? (
                          <>
                            <div>
                              <span style={{ color: "#6b7280" }}>Status: </span>
                              <span
                                style={{ fontWeight: 600, color: "#b91c1c" }}
                              >
                                Failed
                              </span>
                            </div>
                            {subscriptionPopupErrors.length > 0 ? (
                              <div style={{ display: "grid", gap: 4 }}>
                                {subscriptionPopupErrors.map((error, idx) => (
                                  <div key={`subscription-error-${idx}`}>
                                    <span style={{ color: "#6b7280" }}>
                                      Error:{" "}
                                    </span>
                                    <span
                                      style={{
                                        fontWeight: 600,
                                        color: "#b91c1c",
                                        whiteSpace: "pre-wrap",
                                      }}
                                    >
                                      {error || "-"}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div>
                                <span style={{ color: "#6b7280" }}>
                                  Error:{" "}
                                </span>
                                <span
                                  style={{ fontWeight: 600, color: "#b91c1c" }}
                                >
                                  Shell profile failed.
                                </span>
                              </div>
                            )}
                          </>
                        ) : subscriptionPopupEntityDetails.length > 0 ? (
                          <div style={{ display: "grid", gap: 6 }}>
                            {subscriptionPopupEntityDetails.map((entity) => (
                              <div
                                key={`subscription-${entity.key}`}
                                style={{
                                  border: "1px solid #e5e7eb",
                                  borderRadius: 6,
                                  padding: "6px 8px",
                                  display: "grid",
                                  gap: 4,
                                }}
                              >
                                {(() => {
                                  const ephLink = createEPHLink(
                                    entity.entityId,
                                  );

                                  return (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 6,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontWeight: 700,
                                          color: "#374151",
                                        }}
                                      >
                                        {entity.label}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if (ephLink) {
                                            window.open(
                                              ephLink,
                                              "_blank",
                                              "noopener,noreferrer",
                                            );
                                          }
                                        }}
                                        disabled={!ephLink}
                                        aria-label={`View ${entity.label} profile`}
                                        style={{
                                          border: "none",
                                          background: "transparent",
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          padding: 0,
                                          cursor: ephLink
                                            ? "pointer"
                                            : "not-allowed",
                                          opacity: ephLink ? 1 : 0.45,
                                          color: ephLink
                                            ? "#D31710"
                                            : "#9ca3af",
                                        }}
                                      >
                                        <VisibilityIcon sx={{ fontSize: 16 }} />
                                      </button>
                                    </div>
                                  );
                                })()}
                                <div>
                                  <span style={{ color: "#6b7280" }}>
                                    Source:{" "}
                                  </span>
                                  <span style={{ fontWeight: 600 }}>
                                    {mapSourceLabel(entity.source)}
                                  </span>
                                </div>
                                <div>
                                  <span style={{ color: "#6b7280" }}>
                                    Enterprise ID:{" "}
                                  </span>
                                  <span style={{ fontWeight: 600 }}>
                                    {entity.enterpriseId || "-"}
                                  </span>
                                </div>
                                <div>
                                  <span style={{ color: "#6b7280" }}>
                                    Entity ID:{" "}
                                  </span>
                                  <span style={{ fontWeight: 600 }}>
                                    {entity.entityId || "-"}
                                  </span>
                                </div>
                                <div>
                                  <span style={{ color: "#6b7280" }}>
                                    OneKey ID Found In EPH:
                                  </span>
                                  <span style={{ fontWeight: 600 }}>
                                    {isTruthy(entity.foundInEph) ? "Yes" : "No"}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div>
                            <span style={{ fontWeight: 600 }}>
                              No subscription details available.
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {showConsentDetailsSection && (
                      <div
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: 8,
                          padding: "8px 10px",
                          display: "grid",
                          gap: 6,
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 700,
                            color: "#166534",
                          }}
                        >
                          Consent Details
                        </div>

                        {consentPopupResult.consentUploaded !== null && (
                          <div>
                            <span style={{ color: "#6b7280" }}>
                              Consent Uploaded:{" "}
                            </span>
                            <span style={{ fontWeight: 600 }}>
                              {consentPopupResult.consentUploaded
                                ? "Yes"
                                : "No"}
                            </span>
                          </div>
                        )}
                        {consentPopupResult.consentUploadedAt && (
                          <div>
                            <span style={{ color: "#6b7280" }}>
                              Consent Uploaded At:{" "}
                            </span>
                            <span style={{ fontWeight: 600 }}>
                              {consentPopupResult.consentUploadedAt}
                            </span>
                          </div>
                        )}
                        {consentPopupResult.emailConsentStatus !== null && (
                          <div>
                            <span style={{ color: "#6b7280" }}>
                              Email Consent Status:{" "}
                            </span>
                            <span style={{ fontWeight: 600 }}>
                              {consentPopupResult.emailConsentStatus
                                ? "Success"
                                : "Failed"}
                            </span>
                          </div>
                        )}
                        {consentPopupResult.phoneConsentStatus !== null && (
                          <div>
                            <span style={{ color: "#6b7280" }}>
                              Phone Consent Status:{" "}
                            </span>
                            <span style={{ fontWeight: 600 }}>
                              {consentPopupResult.phoneConsentStatus
                                ? "Success"
                                : "Failed"}
                            </span>
                          </div>
                        )}
                        {consentPopupResult.emailConsentError && (
                          <div>
                            <span style={{ color: "#6b7280" }}>
                              Email Consent Error:{" "}
                            </span>
                            <span style={{ fontWeight: 600 }}>
                              {consentPopupResult.emailConsentError}
                            </span>
                          </div>
                        )}
                        {consentPopupResult.phoneConsentError && (
                          <div>
                            <span style={{ color: "#6b7280" }}>
                              Phone Consent Error:{" "}
                            </span>
                            <span style={{ fontWeight: 600 }}>
                              {consentPopupResult.phoneConsentError}
                            </span>
                          </div>
                        )}
                        {!hasConsentPopupDetails && (
                          <div>
                            <span style={{ fontWeight: 600 }}>
                              No consent details available.
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {!showDcrDetailsSection &&
                      !showSubscriptionDetailsSection &&
                      !showConsentDetailsSection && (
                        <div style={{ color: "#6b7280", fontStyle: "italic" }}>
                          No applicable details available.
                        </div>
                      )}
                  </div>
                ) : null}
              </Popover>
            </div>
          </div>
        </Box>

        {/* Help Modal */}
        <Dialog
          open={showHelpModal}
          onClose={() => setShowHelpModal(false)}
          maxWidth="md"
          fullWidth
          sx={{ zIndex: 9500 }}
          PaperProps={{
            sx: {
              borderRadius: "12px",
              maxHeight: "90vh",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.18)",
            },
          }}
        >
          <DialogTitle sx={{ px: 2.5, pt: 2.25, pb: 1.75 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: "16.5px",
                  fontWeight: 700,
                  color: "#111827",
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  letterSpacing: "-0.01em",
                }}
              >
                📋 Results Page
              </Typography>
              <IconButton
                aria-label="Close help dialog"
                onClick={() => setShowHelpModal(false)}
                size="small"
                sx={{ color: "#9ca3af", borderRadius: "4px", p: 0.5 }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ px: 2.5, pb: 2.5, overflow: "auto" }}>
            <Box
              sx={{
                fontSize: "13.5px",
                color: "#374151",
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                whiteSpace: "normal",
                wordSpacing: "normal",
                "& h2": {
                  color: "#1f2937",
                  marginTop: "20px",
                  marginBottom: "10px",
                  fontSize: "14.5px",
                  fontWeight: 600,
                  "&:first-of-type": {
                    marginTop: 0,
                  },
                },
                "& ul": {
                  listStyle: "none",
                  paddingLeft: 0,
                  marginBottom: "12px",
                },
                "& li": {
                  paddingLeft: "22px",
                  marginBottom: "6px",
                  position: "relative",
                  fontSize: "13.5px",
                  lineHeight: 1.5,
                  "&:before": {
                    content: '"→"',
                    position: "absolute",
                    left: 0,
                    color: "#D31710",
                    fontWeight: "bold",
                    fontSize: "22px",
                    fontFamily: "monospace",
                    top: "-8px",
                  },
                },
                "& p": {
                  marginBottom: "12px",
                  lineHeight: 1.6,
                  fontSize: "13.5px",
                },
                "& .tab-info": {
                  backgroundColor: "#fef3c7",
                  borderLeft: "4px solid #f59e0b",
                  padding: "10px 12px",
                  marginBottom: "10px",
                  borderRadius: "4px",
                  fontSize: "13.5px",
                  lineHeight: 1.5,
                },
                "& .button-guide": {
                  backgroundColor: "#f0f9ff",
                  borderLeft: "4px solid #0ea5e9",
                  padding: "10px 12px",
                  marginBottom: "10px",
                  borderRadius: "4px",
                  fontSize: "13.5px",
                  lineHeight: 1.5,
                },
                "& .button-name": {
                  fontWeight: 600,
                  color: "#D31710",
                },
                "& .note": {
                  backgroundColor: "#dbeafe",
                  borderLeft: "4px solid #3b82f6",
                  padding: "10px 12px",
                  marginBottom: "10px",
                  borderRadius: "4px",
                  fontSize: "13.5px",
                  lineHeight: 1.5,
                },
              }}
            >
              <p>
                This page shows the final processing outcome for every record
                you submitted, including DCR, subscription, and consent results.
              </p>

              <h2>🗂️ Understanding the Tabs</h2>
              <div className="tab-info">
                <strong>EPH Tab</strong> - Records that are processed in EPH.
                Note: Even if one entity in a record is found in EPH, the
                complete processing status for that record is shown in the EPH
                tab only.
              </div>
              <div className="tab-info">
                <strong>VENDOR Tab</strong> - Records processed against external
                vendor sources (IQVIA, OpenData, etc.)
              </div>
              <div className="tab-info">
                <strong>NOT FOUND Tab</strong> - Records that had no matching
                profile
              </div>
              <div className="tab-info">
                <strong>NO ACTION Tab</strong> - Records that were not selected
                by user in Review Console Page for further processing
              </div>

              <h2>🔄 Keeping Results Up to Date</h2>
              <ul>
                <li>
                  This page automatically checks for updated results while your
                  records are still being processed
                </li>
                <li>
                  A "Fetching results..." indicator appears at the top while the
                  page is checking for updates
                </li>
                <li>
                  If processing is not completed, a manual "Refresh" button
                  becomes available. It will disappear after processing is done
                  and results have been fetched
                </li>
              </ul>

              <h2>🔍 Viewing Record Details</h2>
              <p>
                Click on the{" "}
                <InfoOutlinedIcon
                  sx={{
                    fontSize: 16,
                    verticalAlign: "middle",
                    color: "#166534",
                  }}
                />{" "}
                icon within a row to view DCR, Subscription, or Consent result
                details, including error messages when applicable.
              </p>

              <h2>🎯 Using the Action Buttons</h2>
              <div className="button-guide">
                <strong className="button-name">Refresh Button</strong>
                <br />
                Manually fetches the latest processing results once available.
              </div>
              <div className="button-guide">
                <strong className="button-name">Export Results Button</strong>
                <br />
                Downloads all results as an Excel file (.xlsx), with separate
                worksheet for each tab (EPH, VENDOR, NOT FOUND, NO ACTION).
              </div>

              <h2>💾 What's Included in the Export</h2>
              <ul>
                <li>Row number and input data</li>
                <li>HCP and HCO names extracted from input</li>
                <li>Selected record and Enterprise IDs</li>
                <li>DCR Status and detailed DCR results</li>
                <li>Subscription result information</li>
                <li>Phone and Email Consent status</li>
                <li>Detailed Consent results</li>
              </ul>
            </Box>
          </DialogContent>
        </Dialog>
      </Card>
    </div>
  );
}